import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, ComposedChart, BarChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { AdminAnalytics, CourtPOI, AthleteProfile, OfflineQueueItem, Sponsor, AuthProviderSettings } from '../types';
import { SponsorManagementTab } from './admin/SponsorManagementTab';
import { EmailLogsTab } from './admin/EmailLogsTab';
import { EmailTemplateEditor } from './admin/EmailTemplateEditor';
import { 
  ShieldAlert, 
  Users, 
  Activity, 
  MapPin, 
  CheckCircle2,
  Server,
  Check,
  X,
  Clock,
  Lock,
  KeyRound,
  Search,
  Megaphone,
  UserX,
  UserCheck,
  RotateCcw,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  LogOut,
  Sparkles,
  Info,
  Key,
  Crown,
  DollarSign,
  Building2,
  CreditCard,
  ArrowUpRight,
  Download,
  CheckCircle,
  Eye,
  EyeOff,
  User,
  WifiOff,
  RefreshCw,
  FileText,
  XCircle,
  Database,
  Chrome,
  Mail,
  Tag,
  Filter,
  Plus,
  Terminal,
  History,
  TrendingUp
} from 'lucide-react';

import { saveUIPreferencesToFirestore } from '../services/firestoreService';
import { ResetEntireSettingsConfirmationModal } from './admin/ResetEntireSettingsConfirmationModal';
import { PaymentProcessor } from '../utils/PaymentProcessor';

export interface AuthActivityLog {
  id: string;
  timestamp: string;
  userEmail: string;
  handle: string;
  provider: 'Google OAuth' | 'Apple ID' | 'Facebook' | 'Email & Password';
  status: 'SUCCESS' | 'BLOCKED_RATE_LIMIT' | 'BLOCKED_WHITELIST' | 'INVALID_PASSWORD';
  ipAddress: string;
  location: string;
}

const generateInitial50AuthLogs = (): AuthActivityLog[] => {
  const users = [
    { email: 'elmejor15@gmail.com', handle: '@elmejor', loc: 'New York, NY (US)' },
    { email: 'jmiller@college.edu', handle: '@jmiller', loc: 'Los Angeles, CA (US)' },
    { email: 'sjenkins@school.org', handle: '@sjenkins', loc: 'Chicago, IL (US)' },
    { email: 'dreyes@university.edu', handle: '@dreyes', loc: 'Houston, TX (US)' },
    { email: 'scout_pro@ncaa.org', handle: '@ncaa_scout', loc: 'Indianapolis, IN (US)' },
    { email: 'erostova@fiba.basketball', handle: '@erostova', loc: 'Madrid, ES' },
    { email: 'vance_m@college.edu', handle: '@mvance', loc: 'Miami, FL (US)' },
    { email: 't_davis@highschool.edu', handle: '@tdavis', loc: 'Atlanta, GA (US)' },
    { email: 'clark_22@college.edu', handle: '@caitlin22', loc: 'Iowa City, IA (US)' },
    { email: 'bronny_j@university.edu', handle: '@bronny', loc: 'Los Angeles, CA (US)' },
  ];

  const providers: ('Google OAuth' | 'Apple ID' | 'Facebook' | 'Email & Password')[] = [
    'Google OAuth', 'Apple ID', 'Facebook', 'Email & Password'
  ];

  const logs: AuthActivityLog[] = [];

  const blockedRateLimitEvents = [
    { idx: 3, email: 'unknown_bot@scraping-net.io', handle: '@unknown', ip: '192.168.1.104', loc: 'Frankfurt, DE' },
    { idx: 12, email: 'credential_stuffing_attempt@proxy.net', handle: '@anon_stuff', ip: '10.0.4.15', loc: 'Moscow, RU' },
    { idx: 27, email: 'bruteforce_runner@attack-vector.org', handle: '@runner_x', ip: '172.16.0.88', loc: 'Shenzhen, CN' },
  ];

  const blockedWhitelistEvents = [
    { idx: 7, email: 'spammer_temp@disposablemail.org', handle: '@disposable_temp', ip: '198.51.100.42', loc: 'Toronto, CA' },
    { idx: 19, email: 'fake_athlete@trashmail.com', handle: '@fake_baller', ip: '203.0.113.19', loc: 'London, UK' },
    { idx: 35, email: 'unauthorized_scout@freemail.biz', handle: '@unauth_scout', ip: '198.51.100.99', loc: 'Paris, FR' },
  ];

  const invalidPassEvents = [
    { idx: 15, email: 'elmejor15@gmail.com', handle: '@elmejor', ip: '192.168.1.42', loc: 'New York, NY (US)' },
    { idx: 41, email: 'jmiller@college.edu', handle: '@jmiller', ip: '172.56.21.90', loc: 'Los Angeles, CA (US)' },
  ];

  for (let i = 0; i < 50; i++) {
    const minutesAgo = (i * 18) + Math.floor(Math.random() * 5);
    const timeStr = minutesAgo < 60 
      ? `${minutesAgo + 1} mins ago` 
      : `${Math.floor(minutesAgo / 60)} hrs ago`;

    const rlMatch = blockedRateLimitEvents.find(e => e.idx === i);
    if (rlMatch) {
      logs.push({
        id: `auth_log_${50 - i}`,
        timestamp: timeStr,
        userEmail: rlMatch.email,
        handle: rlMatch.handle,
        provider: 'Email & Password',
        status: 'BLOCKED_RATE_LIMIT',
        ipAddress: rlMatch.ip,
        location: rlMatch.loc,
      });
      continue;
    }

    const wlMatch = blockedWhitelistEvents.find(e => e.idx === i);
    if (wlMatch) {
      logs.push({
        id: `auth_log_${50 - i}`,
        timestamp: timeStr,
        userEmail: wlMatch.email,
        handle: wlMatch.handle,
        provider: 'Email & Password',
        status: 'BLOCKED_WHITELIST',
        ipAddress: wlMatch.ip,
        location: wlMatch.loc,
      });
      continue;
    }

    const bpMatch = invalidPassEvents.find(e => e.idx === i);
    if (bpMatch) {
      logs.push({
        id: `auth_log_${50 - i}`,
        timestamp: timeStr,
        userEmail: bpMatch.email,
        handle: bpMatch.handle,
        provider: 'Email & Password',
        status: 'INVALID_PASSWORD',
        ipAddress: bpMatch.ip,
        location: bpMatch.loc,
      });
      continue;
    }

    const u = users[i % users.length];
    const prov = providers[i % providers.length];
    logs.push({
      id: `auth_log_${50 - i}`,
      timestamp: timeStr,
      userEmail: u.email,
      handle: u.handle,
      provider: prov,
      status: 'SUCCESS',
      ipAddress: `192.168.${(i % 10) + 1}.${(i * 7) % 250 + 1}`,
      location: u.loc,
    });
  }

  return logs;
};

interface AdminDashboardViewProps {
  analytics: AdminAnalytics;
  courts: CourtPOI[];
  athletes: AthleteProfile[];
  currentUser?: AthleteProfile;
  sponsors?: Sponsor[];
  authProviderSettings?: AuthProviderSettings;
  onUpdateAuthProviderSettings?: (settings: AuthProviderSettings) => void;
  onAddSponsor?: (sponsor: Sponsor) => void;
  onUpdateSponsor?: (sponsorId: string, updates: Partial<Sponsor>) => void;
  onDeleteSponsor?: (sponsorId: string) => void;
  onResetSponsors?: () => void;
  pendingQueue?: OfflineQueueItem[];
  onValidateCourt: (courtId: string, action: 'approve' | 'reject') => void;
  onUpdateUserStatus: (userId: string, status: 'active' | 'suspended' | 'banned', reason?: string) => void;
  onDeleteUser: (userId: string) => void;
  onResetUserPassword: (userId: string) => string;
  onUpgradeUserPro?: (userId: string, isPro: boolean) => void;
  onValidateScoutAccess?: (userId: string, isScoutValidated: boolean) => void;
  onResetUserStats?: (userId: string) => void;
  onResetEntireSite?: () => void;
  isAdminAuthenticated: boolean;
  onAdminLogin: (username: string, pass: string) => { success: boolean; message?: string };
  onAdminLogout: () => void;
}

export const AdminDashboardView: React.FC<AdminDashboardViewProps> = ({
  analytics,
  courts = [],
  athletes = [],
  currentUser,
  sponsors = [],
  authProviderSettings = { googleEnabled: false, facebookEnabled: false, appleEnabled: false },
  onUpdateAuthProviderSettings,
  onAddSponsor = () => {},
  onUpdateSponsor = () => {},
  onDeleteSponsor = () => {},
  onResetSponsors,
  pendingQueue = [],
  onValidateCourt,
  onUpdateUserStatus,
  onDeleteUser,
  onResetUserPassword,
  onUpgradeUserPro,
  onValidateScoutAccess,
  onResetUserStats,
  onResetEntireSite,
  isAdminAuthenticated,
  onAdminLogin,
  onAdminLogout,
}) => {
  // Safe Analytics fallback object
  const safeAnalytics = React.useMemo(() => ({
    totalUsers: analytics?.totalUsers ?? athletes.length,
    activeToday: analytics?.activeToday ?? 0,
    gamesHostedThisWeek: analytics?.gamesHostedThisWeek ?? 0,
    courtCheckinsToday: analytics?.courtCheckinsToday ?? 0,
    topCourts: analytics?.topCourts ?? [],
    concurrencyData: analytics?.concurrencyData ?? [],
    syncSuccessRate: analytics?.syncSuccessRate ?? 99.8,
  }), [analytics, athletes.length]);

  // Mount lifecycle logging
  useEffect(() => {
    try {
      console.log('[AdminDashboardView] Mounted.', {
        isAdminAuthenticated,
        athletesCount: athletes.length,
        courtsCount: courts.length,
        sponsorsCount: sponsors.length,
        pendingQueueCount: pendingQueue.length,
        hasAnalytics: Boolean(analytics),
      });
    } catch (err) {
      console.error('[AdminDashboardView] Error during mount lifecycle:', err);
    }
  }, []);

  // Login Gate state (Username + Password authentication for scale)
  const [inputUsername, setInputUsername] = useState('');
  const [inputPassword, setInputPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  useEffect(() => {
    if (lockoutRemaining > 0) {
      const timer = setInterval(() => {
        setLockoutRemaining((prev) => {
          if (prev <= 1) {
            setFailedAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutRemaining]);

  // Dashboard state
  const [adminSection, setAdminSection] = useState<'users' | 'payouts' | 'analytics' | 'platform_metrics' | 'parks' | 'logs' | 'sync' | 'sponsors' | 'auth_settings' | 'email_logs' | 'email_template'>('users');
  const [parkFilter, setParkFilter] = useState<'pending' | 'verified' | 'all'>('pending');
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'active' | 'suspended' | 'banned'>('all');

  // Admin Session Security Timer State (default 1800s = 30 min session duration)
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number>(1800);
  const [isDismissedSessionWarning, setIsDismissedSessionWarning] = useState<boolean>(false);

  useEffect(() => {
    if (!isAdminAuthenticated) return;

    const timer = setInterval(() => {
      setSessionTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onAdminLogout();
          triggerAuthToast('Session Expired', 'Admin session auto-terminated after inactivity period');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isAdminAuthenticated, onAdminLogout]);

  const handleExtendSession = () => {
    setSessionTimeRemaining(1800); // Reset to 30 mins
    setIsDismissedSessionWarning(false);
    triggerAuthToast('Session Renewed', 'Admin active session extended by +30 minutes');
    setActionNotice('Session renewed for 30 minutes.');
    addAuditLog('Admin extended active security session (+30 minutes)', 'update', 'AUTH_CONFIG');
  };

  const handleSimulateExpiringSession = () => {
    setSessionTimeRemaining(280); // Set countdown to 4 minutes 40 seconds (< 5 min threshold)
    setIsDismissedSessionWarning(false);
    triggerAuthToast('Session Warning Test', 'Set timer to 4m 40s to verify warning modal');
  };

  // Platform Usage Metrics State (30 Days Daily Active Athletes)
  const [platformMetricsDays, setPlatformMetricsDays] = useState<30 | 14 | 7>(30);
  const [platformMetricsMetric, setPlatformMetricsMetric] = useState<'activeAthletes' | 'pickupGames' | 'proUpgrades'>('activeAthletes');
  
  const dailyActiveAthleteData = React.useMemo(() => {
    const rawData = [];
    const baseDate = new Date('2026-08-13');
    for (let i = 29; i >= 0; i--) {
      const d = new Date(baseDate);
      d.setDate(d.getDate() - i);
      const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dayOfWeek = d.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      const growthFactor = (30 - i) * 14;
      const baseActive = isWeekend ? 1120 : 740;
      const activeAthletes = baseActive + Math.floor(Math.random() * 180) + growthFactor;
      const pickupGames = Math.floor(activeAthletes / 18) + Math.floor(Math.random() * 12);
      const proUpgrades = Math.floor(Math.random() * 9) + 4;

      rawData.push({
        date: dateLabel,
        activeAthletes,
        pickupGames,
        proUpgrades,
        isWeekend,
      });
    }

    return rawData.map((item, idx, arr) => {
      const start = Math.max(0, idx - 6);
      const windowItems = arr.slice(start, idx + 1);
      const sumActive = windowItems.reduce((acc, w) => acc + w.activeAthletes, 0);
      const rollingAvg7Day = Math.round(sumActive / windowItems.length);
      return {
        ...item,
        rollingAvg7Day,
      };
    });
  }, []);

  // Offline Sync Management State
  const [syncFilterType, setSyncFilterType] = useState<string>('all');
  const [syncStatusFilter, setSyncStatusFilter] = useState<'all' | 'failed' | 'pending' | 'synced'>('failed');
  const [failedSyncList, setFailedSyncList] = useState<OfflineQueueItem[]>(() => [
    {
      id: 'off_sync_8901',
      type: 'LOG_GAME',
      payload: { athleteId: 'ath_2901', athleteName: 'Jordan Vance', sport: 'volleyball', kills: 14, digs: 8, courtName: 'McCarren Volleyball Court #1' },
      createdAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      status: 'failed',
      errorMessage: '504 Gateway Timeout: Intermittent cellular network loss during payload submission.',
      retryCount: 3,
    },
    {
      id: 'off_sync_8902',
      type: 'POST_CHAT',
      payload: { athleteId: 'ath_1102', athleteName: 'Marcus Vance', teamId: 'tm_so_cal', messageText: 'Who is down for 6v6 sand volleyball doubles at Sunset Beach?' },
      createdAt: new Date(Date.now() - 5 * 3600 * 1000).toISOString(),
      status: 'failed',
      errorMessage: 'Network Disconnect: Client websocket stream dropped mid-transfer.',
      retryCount: 2,
    },
    {
      id: 'off_sync_8903',
      type: 'HOST_GAME',
      payload: { athleteId: 'ath_4490', athleteName: 'Elena Rostova', sport: 'volleyball', title: 'Sunset Beach 2v2 Championship', maxCapacity: 12 },
      createdAt: new Date(Date.now() - 11 * 3600 * 1000).toISOString(),
      status: 'failed',
      errorMessage: 'Auth Token Expired: Background sync triggered after session idle threshold.',
      retryCount: 4,
    },
    {
      id: 'off_sync_8904',
      type: 'UPDATE_PROFILE',
      payload: { athleteId: 'ath_8831', athleteName: 'Sarah Jenkins', bio: 'Beach & Indoor Volleyball Setter | UCLA Club', primarySport: 'volleyball' },
      createdAt: new Date(Date.now() - 18 * 3600 * 1000).toISOString(),
      status: 'failed',
      errorMessage: 'Conflict 409: Database write lock acquired by concurrent sync task.',
      retryCount: 1,
    },
    {
      id: 'off_sync_8905',
      type: 'LOG_GAME',
      payload: { athleteId: 'ath_3302', athleteName: 'David Reyes', sport: 'basketball', pts: 22, ast: 7, courtName: 'Rucker Park Court' },
      createdAt: new Date(Date.now() - 22 * 3600 * 1000).toISOString(),
      status: 'failed',
      errorMessage: '503 Service Unavailable: Remote DB endpoint unreachable on high latency link.',
      retryCount: 5,
    },
    ...pendingQueue,
  ]);

  // Payout Dashboard State
  const [withdrawalBankName, setWithdrawalBankName] = useState('JPMorgan Chase Bank, N.A.');
  const [withdrawalAccountHolder, setWithdrawalAccountHolder] = useState('Playground League Sports Corp');
  const [withdrawalAccountNumber, setWithdrawalAccountNumber] = useState('4839-2019-8842');
  const [withdrawalRoutingNumber, setWithdrawalRoutingNumber] = useState('121000358');
  const [withdrawalSwiftIban, setWithdrawalSwiftIban] = useState('CHASUS33XXX / US89CHAS1210003588842');
  const [withdrawalAmountInput, setWithdrawalAmountInput] = useState('2500');
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  
  const [payoutLogs, setPayoutLogs] = useState<Array<{ id: string; date: string; amount: string; bank: string; method: string; status: string }>>([
    { id: 'w-881', date: 'Aug 04, 2026', amount: '$4,500.00', bank: 'JPMorgan Chase (*8842)', method: 'Stripe Connect Express Wire', status: 'Completed' },
    { id: 'w-880', date: 'Jul 28, 2026', amount: '$3,200.00', bank: 'JPMorgan Chase (*8842)', method: 'PayPal Payouts Direct', status: 'Completed' },
    { id: 'w-879', date: 'Jul 15, 2026', amount: '$5,000.00', bank: 'JPMorgan Chase (*8842)', method: 'Stripe Connect Express Wire', status: 'Completed' },
  ]);

  const [transactionHistory] = useState([
    { id: 'tx-101', athlete: 'Jordan Miller (@jmiller)', plan: 'Monthly PRO Pass ($9.99)', source: 'Stripe Card (Visa)', date: 'Just now', receipt: 'INV-STRIPE-889102' },
    { id: 'tx-102', athlete: 'Marcus Vance (@mvance)', plan: 'Lifetime Pro Badge ($19.99)', source: 'PayPal Smart Buttons', date: '12 mins ago', receipt: 'INV-PP-449120' },
    { id: 'tx-103', athlete: 'Sarah Jenkins (@sjenkins)', plan: 'Monthly PRO Pass ($9.99)', source: 'Apple Pay Express', date: '45 mins ago', receipt: 'INV-STRIPE-773912' },
    { id: 'tx-104', athlete: 'David Reyes (@dreyes)', plan: 'Monthly PRO Pass ($9.99)', source: 'Google Pay Express', date: '2 hours ago', receipt: 'INV-STRIPE-332901' },
    { id: 'tx-105', athlete: 'Elena Rostova (@erostova)', plan: 'Lifetime Pro Badge ($19.99)', source: 'Bank Wire Transfer', date: '5 hours ago', receipt: 'INV-WIRE-991023' },
  ]);

  // Modals & Action feedback state
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [authToast, setAuthToast] = useState<{ id: string; headline: string; message: string; isSuccess: boolean } | null>(null);
  const [localAuthSettings, setLocalAuthSettings] = useState<AuthProviderSettings>(authProviderSettings);
  
  // Stripe API & Checkout Session Diagnostic State
  const [isStripeDiagnosticRunning, setIsStripeDiagnosticRunning] = useState(false);
  const [stripeDiagnosticResult, setStripeDiagnosticResult] = useState<{
    success: boolean;
    sessionId: string;
    url?: string;
    checkoutUrl?: string;
    error?: string;
    isSimulated?: boolean;
    managedPaymentsEnabled?: boolean;
    stripeVersion?: string;
    payload?: any;
    timestamp: string;
  } | null>(null);
  const [showDiagnosticJson, setShowDiagnosticJson] = useState(false);

  const handleRunStripeDiagnostic = async () => {
    setIsStripeDiagnosticRunning(true);
    setActionNotice('Running Stripe Managed Payments Checkout Session Diagnostic...');
    
    // Use current user details if available, or fallback to first athlete / default admin
    const userForDiagnostic = currentUser || athletes[0] || {
      id: 'admin_diagnostic_tester',
      email: 'admin.tester@playgroundleague.pro',
      name: 'Superadmin Diagnostic User',
    };

    try {
      const result = await PaymentProcessor.mockCreateCheckoutSession(userForDiagnostic);
      setStripeDiagnosticResult(result);
      setIsStripeDiagnosticRunning(false);
      
      // Explicitly log to console for debugging and verification
      console.log('[Stripe Diagnostic] Admin Triggered Mock Create Checkout Session Response:', result);
      
      triggerAuthToast('Stripe Diagnostic Succeeded', `Checkout session created: ${result.sessionId} (Managed Payments Enabled)`);
      setActionNotice(`Stripe Diagnostic Success: Session ${result.sessionId} verified with Managed Payments enabled! Check console logs.`);
      addAuditLog(`Admin executed mock Create Checkout Session diagnostic for ${userForDiagnostic.email} - Session ID: ${result.sessionId}`, 'update');
    } catch (err: any) {
      console.error('[Stripe Diagnostic Error]', err);
      setIsStripeDiagnosticRunning(false);
      setActionNotice(`Stripe Diagnostic Failed: ${err?.message || err}`);
    }
  };

  // Tag-based domain whitelist management state
  const [domainTagInput, setDomainTagInput] = useState<string>('');
  const [domainTagSearch, setDomainTagSearch] = useState<string>('');

  // Rate Limiting & Brute-Force Protection State
  const [blockedIPs, setBlockedIPs] = useState<string[]>(['192.168.1.104', '10.0.4.15', '172.16.0.88']);
  const [isResetSettingsConfirmModalOpen, setIsResetSettingsConfirmModalOpen] = useState(false);

  // Authentication Activity Log State (Last 50 events)
  const [authLogs, setAuthLogs] = useState<AuthActivityLog[]>(() => generateInitial50AuthLogs());
  const [authLogFilterStatus, setAuthLogFilterStatus] = useState<'ALL' | 'SUCCESS' | 'BLOCKED'>('ALL');
  const [authLogFilterProvider, setAuthLogFilterProvider] = useState<'ALL' | 'Google' | 'Apple' | 'Facebook' | 'Email'>('ALL');
  const [authLogSearch, setAuthLogSearch] = useState<string>('');

  // 7-Day Login Attempt Frequency Data for Recharts Line Chart
  const loginAttempts7DaysData = React.useMemo(() => {
    const days = ['Aug 7', 'Aug 8', 'Aug 9', 'Aug 10', 'Aug 11', 'Aug 12', 'Aug 13'];
    const successes = [38, 45, 52, 60, 48, 55, 64];
    const failures = [5, 8, 4, 11, 7, 9, 6];

    return days.map((dayLabel, idx) => ({
      day: dayLabel,
      successful: successes[idx],
      failed: failures[idx],
      total: successes[idx] + failures[idx],
    }));
  }, []);

  useEffect(() => {
    setLocalAuthSettings(authProviderSettings);
  }, [authProviderSettings]);

  const triggerAuthToast = (providerName: string, enabled: boolean | string) => {
    const statusText = typeof enabled === 'boolean' ? (enabled ? 'ENABLED' : 'DISABLED') : enabled;
    setAuthToast({
      id: Date.now().toString(),
      headline: 'Auth Provider Updated',
      message: `${providerName} setting updated to [${statusText}]. Saved to Firestore 'settings' collection.`,
      isSuccess: true,
    });
    setTimeout(() => {
      setAuthToast(null);
    }, 4500);
  };

  const handleResetBlockedIPs = () => {
    const count = blockedIPs.length;
    setBlockedIPs([]);
    setAuthToast({
      id: Date.now().toString(),
      headline: 'Blocked IPs Cleared',
      message: `Successfully unblocked ${count} restricted IP address(es) and reset rate-limiting violation counters to zero.`,
      isSuccess: true,
    });
    setTimeout(() => setAuthToast(null), 4500);
    setActionNotice(`Reset Brute-Force Rate Limiter: Cleared ${count} blocked IP addresses.`);
    addAuditLog(`Admin reset brute-force rate limiter & unblocked ${count} IP addresses`, 'update');
  };

  const handleAddDomainTag = (tagToAdd?: string) => {
    const raw = tagToAdd ?? domainTagInput;
    if (!raw.trim()) return;
    const formatted = raw.trim().toLowerCase().startsWith('@')
      ? raw.trim().toLowerCase()
      : `@${raw.trim().toLowerCase()}`;

    const current = localAuthSettings.allowedEmailDomains || [];
    if (current.includes(formatted)) {
      setDomainTagInput('');
      return;
    }

    const updatedDomains = [...current, formatted];
    const updatedSettings: AuthProviderSettings = {
      ...localAuthSettings,
      allowedEmailDomains: updatedDomains,
    };
    setLocalAuthSettings(updatedSettings);
    setDomainTagInput('');
    onUpdateAuthProviderSettings?.(updatedSettings);
    triggerAuthToast('Email Whitelist Tag Added', formatted);
    setActionNotice(`Added Email Whitelist Tag: ${formatted}`);
    addAuditLog(`Admin added whitelisted email domain tag: ${formatted}`, 'update');
  };

  const handleRemoveDomainTag = (tagToRemove: string) => {
    const current = localAuthSettings.allowedEmailDomains || [];
    const updatedDomains = current.filter(t => t !== tagToRemove);
    const updatedSettings: AuthProviderSettings = {
      ...localAuthSettings,
      allowedEmailDomains: updatedDomains,
    };
    setLocalAuthSettings(updatedSettings);
    onUpdateAuthProviderSettings?.(updatedSettings);
    triggerAuthToast('Email Whitelist Tag Removed', tagToRemove);
    setActionNotice(`Removed Email Whitelist Tag: ${tagToRemove}`);
    addAuditLog(`Admin removed whitelisted email domain tag: ${tagToRemove}`, 'update');
  };

  const handleClearAllTags = () => {
    const updatedSettings: AuthProviderSettings = {
      ...localAuthSettings,
      allowedEmailDomains: [],
    };
    setLocalAuthSettings(updatedSettings);
    onUpdateAuthProviderSettings?.(updatedSettings);
    triggerAuthToast('Email Whitelist Cleared', 'All email domains permitted');
    setActionNotice(`Cleared all email domain whitelist tags.`);
    addAuditLog(`Admin cleared all email domain whitelist tags`, 'update');
  };

  const handleSimulateAuthLog = () => {
    const providers: ('Google OAuth' | 'Apple ID' | 'Facebook' | 'Email & Password')[] = [
      'Google OAuth', 'Apple ID', 'Facebook', 'Email & Password'
    ];
    const statuses: ('SUCCESS' | 'BLOCKED_RATE_LIMIT' | 'BLOCKED_WHITELIST' | 'INVALID_PASSWORD')[] = [
      'SUCCESS', 'SUCCESS', 'SUCCESS', 'BLOCKED_RATE_LIMIT', 'BLOCKED_WHITELIST'
    ];
    const randProvider = providers[Math.floor(Math.random() * providers.length)];
    const randStatus = statuses[Math.floor(Math.random() * statuses.length)];
    const newEvent: AuthActivityLog = {
      id: `auth_log_${Date.now()}`,
      timestamp: 'Just now',
      userEmail: randStatus === 'BLOCKED_WHITELIST' ? 'athlete@unauthorized-domain.com' : 'simulated_user@playgrounds.app',
      handle: randStatus === 'BLOCKED_WHITELIST' ? '@unauthorized' : '@sim_user',
      provider: randProvider,
      status: randStatus,
      ipAddress: randStatus === 'BLOCKED_RATE_LIMIT' ? '192.168.1.104' : `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      location: 'New York, NY (US)',
    };

    setAuthLogs(prev => [newEvent, ...prev.slice(0, 49)]);
    triggerAuthToast('New Auth Event Logged', `${randProvider} -> ${randStatus}`);
  };

  const handleToggleProvider = (key: 'googleEnabled' | 'facebookEnabled' | 'appleEnabled', name: string) => {
    const nextVal = !localAuthSettings[key];
    const updated: AuthProviderSettings = {
      ...localAuthSettings,
      [key]: nextVal,
    };
    setLocalAuthSettings(updated);
    triggerAuthToast(name, nextVal);
    setActionNotice(`Auth Provider Updated: ${name} ${nextVal ? 'Enabled' : 'Disabled'} & saved to Firestore 'settings' collection`);
    onUpdateAuthProviderSettings?.(updated);
    addAuditLog(`Admin ${nextVal ? 'enabled' : 'disabled'} ${name} OAuth provider`, 'update');
  };

  const handleResetToDefaultAuthSettings = () => {
    const defaultSettings: AuthProviderSettings = {
      ...localAuthSettings,
      googleEnabled: true,
      facebookEnabled: true,
      appleEnabled: true,
      providerOrder: ['google', 'facebook', 'apple'],
    };
    setLocalAuthSettings(defaultSettings);
    onUpdateAuthProviderSettings?.(defaultSettings);
    triggerAuthToast('Default Settings Restored', true);
    setActionNotice('Reverted Google, Facebook, and Apple authentication toggles to default enabled state in Firestore.');
    addAuditLog('Admin reset Google, Facebook, and Apple authentication provider toggles to default enabled state', 'update');
  };

  const handleResetUIPreferences = () => {
    const defaultSettings: AuthProviderSettings = {
      ...localAuthSettings,
      googleEnabled: true,
      facebookEnabled: true,
      appleEnabled: true,
      providerOrder: ['google', 'facebook', 'apple'],
      strictRateLimitingEnabled: false,
      autoSuspensionEnabled: true,
      maxFailedAttemptsLimit: 5,
      allowedEmailDomains: [],
    };
    setLocalAuthSettings(defaultSettings);
    onUpdateAuthProviderSettings?.(defaultSettings);
    saveUIPreferencesToFirestore({
      proIconEnabled: false,
      onlineSyncVisible: false,
      miniGamesDefaultEnabled: false,
      resetAt: new Date().toISOString(),
      updatedBy: 'Admin',
    });
    triggerAuthToast('UI Preferences Reset', 'Default State Restored');
    setActionNotice('Reset all UI preferences & feature toggles (Online Sync, Pro Icon, Auth Toggles) to default configuration state and saved to Firestore immediately. 🔄');
    addAuditLog('Admin reset all UI preferences and auth toggles to default configuration state in Firestore', 'update');
  };

  const handleResetEntireApplicationSettings = () => {
    const defaultSettings: AuthProviderSettings = {
      googleEnabled: true,
      facebookEnabled: true,
      appleEnabled: true,
      providerOrder: ['google', 'facebook', 'apple'],
      strictRateLimitingEnabled: false,
      autoSuspensionEnabled: true,
      maxFailedAttemptsLimit: 5,
      allowedEmailDomains: [],
    };
    setLocalAuthSettings(defaultSettings);
    onUpdateAuthProviderSettings?.(defaultSettings);
    setBlockedIPs([]);

    saveUIPreferencesToFirestore({
      proIconEnabled: false,
      onlineSyncVisible: false,
      miniGamesDefaultEnabled: false,
      resetAt: new Date().toISOString(),
      updatedBy: 'Admin (Reset Entire Application Settings)',
    });

    triggerAuthToast('Entire App Settings Reset', 'All Defaults Restored in Firestore');
    setActionNotice('Reverted all toggles, whitelists, rate limits, and thresholds to original application defaults in Firestore database 🔄');
    addAuditLog('Admin reset ENTIRE application settings, toggles, whitelists, rate limits, and thresholds to original defaults in Firestore', 'reset');
  };

  const handleToggleRateLimiting = () => {
    const nextVal = !localAuthSettings.strictRateLimitingEnabled;
    const updated: AuthProviderSettings = {
      ...localAuthSettings,
      strictRateLimitingEnabled: nextVal,
    };
    setLocalAuthSettings(updated);
    triggerAuthToast('Strict Auth Rate Limiting', nextVal);
    setActionNotice(`Strict Rate Limiting ${nextVal ? 'Activated' : 'Deactivated'} & saved to Firestore 'settings' collection`);
    onUpdateAuthProviderSettings?.(updated);
    addAuditLog(`Admin ${nextVal ? 'enabled' : 'disabled'} strict auth rate limiting`, 'update');
  };

  const handleSaveWhitelist = (overrideDomains?: string[]) => {
    const domainsToSave = overrideDomains ?? (localAuthSettings.allowedEmailDomains || []);
    
    const updated: AuthProviderSettings = {
      ...localAuthSettings,
      allowedEmailDomains: domainsToSave,
    };
    setLocalAuthSettings(updated);
    triggerAuthToast('Email Domain Whitelist', domainsToSave.length > 0 ? `${domainsToSave.length} Domains Active` : 'All Domains Allowed');
    setActionNotice(`Updated Email Domain Whitelist: ${domainsToSave.length > 0 ? domainsToSave.join(', ') : 'All Domains Allowed'}`);
    onUpdateAuthProviderSettings?.(updated);
    addAuditLog(`Admin updated email domain whitelist: ${domainsToSave.join(', ') || 'Any domain'}`, 'update');
  };

  const handleMoveProviderOrder = (providerKey: string, direction: 'up' | 'down') => {
    const currentOrder = localAuthSettings.providerOrder && localAuthSettings.providerOrder.length > 0
      ? [...localAuthSettings.providerOrder]
      : ['google', 'facebook', 'apple'];
    
    const idx = currentOrder.indexOf(providerKey);
    if (idx === -1) return;

    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= currentOrder.length) return;

    // Swap
    const temp = currentOrder[idx];
    currentOrder[idx] = currentOrder[targetIdx];
    currentOrder[targetIdx] = temp;

    const updated: AuthProviderSettings = {
      ...localAuthSettings,
      providerOrder: currentOrder,
    };
    setLocalAuthSettings(updated);
    triggerAuthToast(`Provider Display Order`, `Order: ${currentOrder.join(' -> ').toUpperCase()}`);
    setActionNotice(`Updated provider display order: ${currentOrder.join(', ')}`);
    onUpdateAuthProviderSettings?.(updated);
    addAuditLog(`Admin reordered auth buttons: ${currentOrder.join(', ')}`, 'update');
  };
  const [tempPasswordModal, setTempPasswordModal] = useState<{ userName: string; tempPass: string } | null>(null);
  const [suspendModalUser, setSuspendModalUser] = useState<AthleteProfile | null>(null);
  const [grantProModalUser, setGrantProModalUser] = useState<AthleteProfile | null>(null);
  const [revokeProModalUser, setRevokeProModalUser] = useState<AthleteProfile | null>(null);
  const [suspendReason, setSuspendReason] = useState('Violation of community guidelines');
  const [isSiteResetModalOpen, setIsSiteResetModalOpen] = useState(false);
  const [siteResetConfirmInput, setSiteResetConfirmInput] = useState('');
  const [siteResetError, setSiteResetError] = useState('');
  // Admin Audit Log State & Search Filters
  const [auditLogSearch, setAuditLogSearch] = useState<string>('');
  const [auditLogCategoryFilter, setAuditLogCategoryFilter] = useState<string>('ALL');

  const [auditLogs, setAuditLogs] = useState<Array<{
    id: string;
    time: string;
    action: string;
    adminEmail: string;
    type: 'ban' | 'suspend' | 'reset' | 'delete' | 'park' | 'update';
    category: 'AUTH_CONFIG' | 'USER_MGMT' | 'SYSTEM_RESET' | 'SECURITY_THRESHOLD' | 'GENERAL';
  }>>([
    { id: 'l1', time: 'Just now', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Superadmin portal session initialized securely', type: 'park', category: 'AUTH_CONFIG' },
    { id: 'l2', time: '8 mins ago', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Updated Security Threshold: Max Failed Attempts set to 5 (Auto-Suspension: Active)', type: 'update', category: 'SECURITY_THRESHOLD' },
    { id: 'l3', time: '22 mins ago', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Cleared brute-force IP rate limiter violation cache (3 blocked IPs restored)', type: 'reset', category: 'SECURITY_THRESHOLD' },
    { id: 'l4', time: '1 hour ago', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Verified Holcombe Rucker Park court #1 venue listing', type: 'park', category: 'USER_MGMT' },
    { id: 'l5', time: '3 hours ago', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Updated Email Domain Whitelist tags (@college.edu, @school.org, @ncaa.org)', type: 'update', category: 'AUTH_CONFIG' },
    { id: 'l6', time: '5 hours ago', adminEmail: 'elmejor15@gmail.com (Superadmin)', action: 'Granted PRO Tier membership to athlete Jordan Vance (@jvance)', type: 'reset', category: 'USER_MGMT' },
  ]);

  const addAuditLog = (
    action: string,
    type: 'ban' | 'suspend' | 'reset' | 'delete' | 'park' | 'update',
    category: 'AUTH_CONFIG' | 'USER_MGMT' | 'SYSTEM_RESET' | 'SECURITY_THRESHOLD' | 'GENERAL' = 'GENERAL',
    adminEmail = 'elmejor15@gmail.com (Superadmin)'
  ) => {
    setAuditLogs((prev) => [
      { id: Date.now().toString(), time: 'Just now', action, adminEmail, type, category },
      ...prev,
    ]);
  };

  // Real-Time Live Security Ticker computation (Last 5 events)
  const liveTickerEvents = React.useMemo(() => {
    const list: Array<{
      id: string;
      time: string;
      title: string;
      detail: string;
      badge: 'RATE_LIMIT' | 'DOMAIN_BLOCKED' | 'BAD_CRED' | 'ADMIN_ACTION' | 'SUCCESS';
    }> = [];

    authLogs.slice(0, 5).forEach((al) => {
      let badge: 'RATE_LIMIT' | 'DOMAIN_BLOCKED' | 'BAD_CRED' | 'SUCCESS' = 'SUCCESS';
      if (al.status === 'BLOCKED_RATE_LIMIT') badge = 'RATE_LIMIT';
      else if (al.status === 'BLOCKED_WHITELIST') badge = 'DOMAIN_BLOCKED';
      else if (al.status === 'INVALID_PASSWORD') badge = 'BAD_CRED';

      list.push({
        id: `ticker_${al.id}`,
        time: al.timestamp,
        title: `${al.userEmail} (${al.provider})`,
        detail: `${al.ipAddress} • ${al.location}`,
        badge,
      });
    });

    auditLogs.slice(0, 3).forEach((audit) => {
      list.push({
        id: `ticker_audit_${audit.id}`,
        time: audit.time,
        title: audit.action,
        detail: audit.adminEmail,
        badge: 'ADMIN_ACTION',
      });
    });

    return list.slice(0, 5);
  }, [authLogs, auditLogs]);

  const handleGateLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (lockoutRemaining > 0) return;

    if (!inputUsername.trim() || !inputPassword.trim()) {
      setLoginError('Please enter both Superadmin Username and Password.');
      return;
    }

    const res = onAdminLogin(inputUsername, inputPassword);
    if (res.success) {
      setLoginError('');
      setInputUsername('');
      setInputPassword('');
      setFailedAttempts(0);
      addAuditLog(`Superadmin (@${inputUsername.trim() || 'superadmin'}) authenticated securely via /superadmin portal`, 'park');
    } else {
      const nextFailed = failedAttempts + 1;
      setFailedAttempts(nextFailed);
      if (nextFailed >= 5) {
        setLockoutRemaining(60);
        setLoginError('🛡️ Security Lockout Triggered: 5 failed login attempts. Superadmin Portal locked for 60 seconds.');
      } else {
        setLoginError(`${res.message || 'Invalid Superadmin credentials.'} (${5 - nextFailed} attempt(s) remaining before security lockout)`);
      }
    }
  };

  // User Stats
  const activeCount = athletes.filter((a) => (a.accountStatus || 'active') === 'active').length;
  const suspendedCount = athletes.filter((a) => a.accountStatus === 'suspended').length;
  const bannedCount = athletes.filter((a) => a.accountStatus === 'banned').length;

  // Filtered Users
  const filteredUsers = athletes.filter((athlete) => {
    const status = athlete.accountStatus || 'active';
    const matchesFilter = userStatusFilter === 'all' || status === userStatusFilter;
    const query = userSearch.toLowerCase();
    const matchesSearch =
      athlete.name.toLowerCase().includes(query) ||
      athlete.handle.toLowerCase().includes(query) ||
      athlete.email.toLowerCase().includes(query) ||
      athlete.primarySport.toLowerCase().includes(query) ||
      athlete.role.toLowerCase().includes(query);
    return matchesFilter && matchesSearch;
  });

  // Park Queues
  const pendingCourts = courts.filter((c) => c.status === 'pending');
  const verifiedCourts = courts.filter((c) => c.status !== 'pending' && c.status !== 'rejected');
  const displayCourts = parkFilter === 'pending' ? pendingCourts : parkFilter === 'verified' ? verifiedCourts : courts;

  // Geo API & Duplicate Venue Validation State
  const [geoAuditResults, setGeoAuditResults] = useState<Record<string, {
    isValidGeo: boolean;
    duplicateWarning?: string;
    distanceToNearestMi?: number;
    nearestCourtName?: string;
    checkedAt: string;
  }>>({});

  const handleRunGeoAudit = (courtToAudit: CourtPOI) => {
    let nearestDist = Infinity;
    let nearestName = '';

    // Calculate Haversine distance to all other courts in miles
    courts.forEach((c) => {
      if (c.id === courtToAudit.id) return;
      const lat1 = courtToAudit.lat;
      const lon1 = courtToAudit.lng;
      const lat2 = c.lat;
      const lon2 = c.lng;
      if (!lat1 || !lon1 || !lat2 || !lon2) return;

      const R = 3958.8; // Radius of the Earth in miles
      const dLat = (lat2 - lat1) * (Math.PI / 180);
      const dLon = (lon2 - lon1) * (Math.PI / 180);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      if (dist < nearestDist) {
        nearestDist = dist;
        nearestName = c.name;
      }
    });

    const isNearDuplicate = nearestDist < 0.3; // Flag if within 0.3 miles
    const isValidCoords =
      typeof courtToAudit.lat === 'number' &&
      typeof courtToAudit.lng === 'number' &&
      courtToAudit.lat >= -90 &&
      courtToAudit.lat <= 90 &&
      courtToAudit.lng >= -180 &&
      courtToAudit.lng <= 180 &&
      (courtToAudit.lat !== 0 || courtToAudit.lng !== 0);

    setGeoAuditResults((prev) => ({
      ...prev,
      [courtToAudit.id]: {
        isValidGeo: isValidCoords,
        duplicateWarning: isNearDuplicate ? `⚠️ Potential Duplicate: "${nearestName}" is only ${nearestDist.toFixed(2)} miles away!` : undefined,
        distanceToNearestMi: nearestDist < 1000 ? nearestDist : undefined,
        nearestCourtName: nearestName,
        checkedAt: new Date().toLocaleTimeString(),
      },
    }));
  };

  // ==========================================
  // UNAUTHENTICATED ADMIN GATE
  // ==========================================
  if (!isAdminAuthenticated) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4 animate-fadeIn text-white">
        <div className="bg-indigo-950/90 border border-white/10 p-8 rounded-[2.5rem] shadow-2xl space-y-6 text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-1 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-full text-xs font-black italic uppercase tracking-wider">
            <ShieldAlert className="w-3.5 h-3.5" />
            <span>Superadmin Access (/superadmin)</span>
          </div>

          <div className="w-16 h-16 bg-rose-500/20 text-rose-400 rounded-3xl flex items-center justify-center mx-auto border border-rose-500/30 shadow-lg shadow-rose-500/10">
            <ShieldAlert className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-black italic uppercase text-white tracking-tight">
              Superadmin Oversight Gate
            </h1>
            <p className="text-xs text-indigo-200/70 font-semibold max-w-md mx-auto">
              Secured single-entry point. Standard athlete accounts do not grant executive administrative credentials. Authenticate with verified username & password.
            </p>
          </div>

          <form onSubmit={handleGateLogin} className="space-y-4 text-left pt-2">
            {/* Username Input */}
            <div>
              <label className="block text-[11px] font-black uppercase text-indigo-300 italic mb-1.5">
                Superadmin Username
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-indigo-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={inputUsername}
                  onChange={(e) => setInputUsername(e.target.value)}
                  placeholder="e.g. superadmin"
                  disabled={lockoutRemaining > 0}
                  className="w-full bg-indigo-900/80 border border-white/10 rounded-2xl pl-10 pr-4 py-2.5 text-sm font-bold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400 disabled:opacity-50"
                  required
                />
              </div>
            </div>

            {/* Password Input with Visibility Toggle */}
            <div>
              <label className="block text-[11px] font-black uppercase text-indigo-300 italic mb-1.5">
                Superadmin Security Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-indigo-400 absolute left-3.5 top-3" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={inputPassword}
                  onChange={(e) => setInputPassword(e.target.value)}
                  placeholder="Enter superadmin password..."
                  disabled={lockoutRemaining > 0}
                  className="w-full bg-indigo-900/80 border border-white/10 rounded-2xl pl-10 pr-10 py-2.5 text-sm font-bold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400 disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-3 text-indigo-400 hover:text-white transition"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3 bg-rose-500/20 border border-rose-500/40 rounded-xl text-xs text-rose-300 font-bold flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}

            {lockoutRemaining > 0 ? (
              <div className="p-3.5 bg-amber-500/20 border border-amber-500/40 rounded-2xl text-center text-xs font-mono font-bold text-amber-300 flex items-center justify-center space-x-2">
                <Clock className="w-4 h-4 animate-spin text-amber-400" />
                <span>Security Lockout: Retry in {lockoutRemaining}s</span>
              </div>
            ) : (
              <button
                type="submit"
                className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center justify-center space-x-2 text-sm"
              >
                <KeyRound className="w-4 h-4" />
                <span>Authenticate Superadmin Session</span>
              </button>
            )}
          </form>
        </div>
      </div>
    );
  }

  // ==========================================
  // AUTHENTICATED ADMIN OVERSIGHT VIEW
  // ==========================================
  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Top Admin Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-indigo-900/80 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-lime-400 text-black rounded-2xl font-black">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-black italic uppercase tracking-tight text-white">
                Admin Oversight Center
              </h1>
              <span className="px-2.5 py-0.5 rounded-full bg-lime-400 text-black text-[10px] font-black uppercase italic">
                SUPER ADMIN
              </span>
            </div>
            <p className="text-xs text-indigo-200/70 mt-0.5 font-semibold">
              Platform user directory, account bans & suspensions, password resets, and park verification queue.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="hidden sm:flex items-center space-x-2 bg-indigo-950 px-3 py-1.5 rounded-2xl border border-white/10 text-xs font-mono text-indigo-300">
            <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span>Session: {Math.floor(sessionTimeRemaining / 60)}m {(sessionTimeRemaining % 60).toString().padStart(2, '0')}s</span>
            <button
              type="button"
              onClick={handleSimulateExpiringSession}
              title="Simulate session expiring warning modal (<5m)"
              className="ml-1 px-2 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded text-[9px] font-bold uppercase transition"
            >
              Test Warning
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-2 bg-indigo-950 px-3 py-1.5 rounded-2xl border border-white/10 text-xs font-mono text-indigo-300">
            <Server className="w-3.5 h-3.5 text-lime-400" />
            <span>admin@playgrounds.app</span>
          </div>

          <button
            onClick={() => {
              setSiteResetError('');
              setSiteResetConfirmInput('');
              setIsSiteResetModalOpen(true);
            }}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-black italic uppercase text-xs rounded-2xl border border-rose-400 shadow-lg shadow-rose-600/30 transition flex items-center space-x-1.5"
            title="Reset all stats and remove all users for live release"
          >
            <RotateCcw className="w-4 h-4" />
            <span>RESET SITE (LIVE RELEASE WIPE) 💥</span>
          </button>

          <button
            onClick={onAdminLogout}
            className="px-4 py-2 bg-rose-500/20 hover:bg-rose-500 text-rose-300 hover:text-white border border-rose-500/40 rounded-2xl font-black italic uppercase text-xs transition flex items-center space-x-1.5"
          >
            <LogOut className="w-4 h-4" />
            <span>Lock Admin Portal</span>
          </button>
        </div>
      </div>

      {/* Session Expiry Warning Modal (Triggers when session remaining <= 5 mins) */}
      {isAdminAuthenticated && sessionTimeRemaining > 0 && sessionTimeRemaining <= 300 && !isDismissedSessionWarning && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="max-w-md w-full bg-indigo-950 border-2 border-amber-400 rounded-3xl p-6 shadow-2xl space-y-5 text-white animate-bounceIn">
            <div className="flex items-center space-x-3 border-b border-white/10 pb-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-400/20 border border-amber-400/40 text-amber-400 flex items-center justify-center shrink-0 font-bold shadow-lg animate-pulse">
                <Clock className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="font-black italic uppercase text-lg text-white">Session Expiring Soon!</h3>
                  <span className="px-2 py-0.5 rounded bg-rose-500 text-white font-mono text-[9px] font-black uppercase">
                    SECURITY WARNING
                  </span>
                </div>
                <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                  Your administrative oversight session is within 5 minutes of expiring.
                </p>
              </div>
            </div>

            <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 text-center space-y-2">
              <div className="text-[10px] font-mono text-indigo-300 uppercase tracking-wider font-bold">
                Automatic Session Timeout In
              </div>
              <div className="text-4xl font-black font-mono text-amber-300 tracking-wider">
                {Math.floor(sessionTimeRemaining / 60)}:{(sessionTimeRemaining % 60).toString().padStart(2, '0')}
              </div>
              <p className="text-[11px] text-indigo-200/70 font-medium leading-snug">
                To safeguard the executive oversight dashboard, inactive sessions terminate automatically when time reaches zero.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleExtendSession}
                className="w-full sm:flex-1 py-3 bg-amber-400 hover:bg-amber-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-amber-400/20 transition flex items-center justify-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Extend Session (+30 Mins)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  onAdminLogout();
                  triggerAuthToast('Logged Out', 'Admin session closed manually');
                }}
                className="w-full sm:w-auto px-4 py-3 bg-indigo-900 hover:bg-rose-600 text-indigo-200 hover:text-white font-black italic uppercase text-xs rounded-xl transition border border-white/10"
              >
                <span>Logout Now</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Security Ticker (Real-Time Overview of Last 5 Security Events) */}
      <div className="bg-indigo-950/90 border border-white/10 rounded-2xl p-4 shadow-xl space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2.5">
          <div className="flex items-center space-x-2.5">
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500"></span>
            </span>
            <div className="flex items-center space-x-2">
              <h3 className="text-xs font-black italic uppercase tracking-wider text-white">
                Live Security Ticker
              </h3>
              <span className="px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[9px] font-black uppercase font-mono">
                REAL-TIME (LAST 5 EVENTS)
              </span>
            </div>
          </div>
          <span className="text-[10px] font-mono text-indigo-300/70">
            Immediate overview of potential unauthorized access patterns & admin activity
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
          {liveTickerEvents.map((evt) => {
            let badgeStyle = "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
            let badgeLabel = "200 OK";

            if (evt.badge === 'RATE_LIMIT') {
              badgeStyle = "bg-amber-500/20 text-amber-300 border-amber-500/40";
              badgeLabel = "429 RATE LIMIT";
            } else if (evt.badge === 'DOMAIN_BLOCKED') {
              badgeStyle = "bg-purple-500/20 text-purple-300 border-purple-500/40";
              badgeLabel = "403 DOMAIN BLOCKED";
            } else if (evt.badge === 'BAD_CRED') {
              badgeStyle = "bg-rose-500/20 text-rose-300 border-rose-500/40";
              badgeLabel = "401 BAD CRED";
            } else if (evt.badge === 'ADMIN_ACTION') {
              badgeStyle = "bg-lime-400/20 text-lime-300 border-lime-400/40";
              badgeLabel = "ADMIN ACTION";
            }

            return (
              <div
                key={evt.id}
                className="p-2.5 bg-indigo-900/50 hover:bg-indigo-900/80 border border-white/10 rounded-xl transition flex flex-col justify-between space-y-1.5"
              >
                <div className="flex items-center justify-between gap-1 text-[9px] font-mono">
                  <span className={`px-1.5 py-0.5 rounded border font-black uppercase ${badgeStyle}`}>
                    {badgeLabel}
                  </span>
                  <span className="text-indigo-300/60 shrink-0">{evt.time}</span>
                </div>

                <div className="text-[11px] font-bold text-white truncate" title={evt.title}>
                  {evt.title}
                </div>

                <div className="text-[9px] font-mono text-indigo-300/70 truncate" title={evt.detail}>
                  {evt.detail}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Notice Alert */}
      {actionNotice && (
        <div className="p-4 bg-lime-400/20 border border-lime-400/40 rounded-2xl text-lime-300 text-xs font-black italic uppercase flex items-center justify-between animate-fadeIn">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-lime-400" />
            <span>{actionNotice}</span>
          </div>
          <button onClick={() => setActionNotice(null)} className="text-lime-300 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Global Auth Provider Toast Notification Service */}
      {authToast && (
        <div className="fixed top-6 right-6 z-50 animate-bounceIn max-w-md bg-indigo-950/95 border-2 border-lime-400 shadow-2xl p-4 rounded-2xl backdrop-blur-xl text-white flex items-start justify-between space-x-3">
          <div className="flex items-start space-x-3">
            <div className="p-2 rounded-xl bg-lime-400 text-black shrink-0">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-black italic uppercase text-lime-400 tracking-wider">
                  {authToast.headline}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[9px] font-black uppercase">
                  FIRESTORE SYNCED
                </span>
              </div>
              <p className="text-xs text-white font-semibold mt-1 leading-snug">
                {authToast.message}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAuthToast(null)}
            className="p-1 text-indigo-300 hover:text-white rounded-lg hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Sub-Navigation Tabs */}
      <div className="flex items-center space-x-2 bg-indigo-950 p-1.5 rounded-2xl border border-white/10 overflow-x-auto">
        <button
          onClick={() => setAdminSection('users')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'users' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>User Directory ({athletes.length})</span>
        </button>

        <button
          onClick={() => setAdminSection('payouts')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'payouts' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
          }`}
        >
          <DollarSign className="w-4 h-4" />
          <span>Payout & Bank Dashboard 💰</span>
        </button>

        <button
          onClick={() => setAdminSection('analytics')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'analytics' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Metrics & Concurrency</span>
        </button>

        <button
          onClick={() => setAdminSection('platform_metrics')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'platform_metrics' ? 'bg-lime-400 text-black shadow-md' : 'text-lime-300 hover:text-white'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-lime-400" />
          <span>Platform Usage Metrics 📊</span>
        </button>

        <button
          onClick={() => setAdminSection('parks')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'parks' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
          }`}
        >
          <MapPin className="w-4 h-4" />
          <span>Park Verification ({pendingCourts.length} Pending)</span>
        </button>

        <button
          onClick={() => setAdminSection('logs')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'logs' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
          }`}
        >
          <Info className="w-4 h-4" />
          <span>Audit Logs ({auditLogs.length})</span>
        </button>

        <button
          onClick={() => setAdminSection('sync')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'sync' ? 'bg-lime-400 text-black shadow-md' : 'text-amber-300 hover:text-white'
          }`}
        >
          <WifiOff className="w-4 h-4" />
          <span>Offline Sync Issues ⚡ ({failedSyncList.filter(i => i.status === 'failed').length})</span>
        </button>

        <button
          onClick={() => setAdminSection('sponsors')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'sponsors' ? 'bg-lime-400 text-black shadow-md' : 'text-lime-300 hover:text-white'
          }`}
        >
          <Megaphone className="w-4 h-4" />
          <span>Sponsors & Ad Control 📣 ({sponsors.length})</span>
        </button>

        <button
          onClick={() => setAdminSection('auth_settings')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'auth_settings' ? 'bg-lime-400 text-black shadow-md' : 'text-amber-300 hover:text-white'
          }`}
        >
          <Lock className="w-4 h-4 text-amber-400" />
          <span>Auth & Social Logins 🔐</span>
        </button>

        <button
          onClick={() => setAdminSection('email_logs')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'email_logs' ? 'bg-lime-400 text-black shadow-md' : 'text-lime-300 hover:text-white'
          }`}
        >
          <Mail className="w-4 h-4 text-lime-400" />
          <span>Email Logs & SMTP 📧</span>
        </button>

        <button
          onClick={() => setAdminSection('email_template')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 shrink-0 ${
            adminSection === 'email_template' ? 'bg-lime-400 text-black shadow-md' : 'text-amber-300 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 text-amber-400" />
          <span>Email Template Editor 📝</span>
        </button>
      </div>

      {/* ================= SECTION 1: USER MANAGEMENT DIRECTORY ================= */}
      {adminSection === 'users' && (
        <div className="space-y-6">
          {/* User Metrics Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 block">
                Total Athletes
              </span>
              <div className="text-2xl font-black italic text-white mt-1">
                {athletes.length.toLocaleString()}
              </div>
              <span className="text-[10px] text-lime-400 font-bold mt-1 block">Registered in Database</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 block">
                Active Status
              </span>
              <div className="text-2xl font-black italic text-lime-400 mt-1">
                {activeCount}
              </div>
              <span className="text-[10px] text-lime-300 font-bold mt-1 block">Full Platform Access</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 block">
                Suspended Users
              </span>
              <div className="text-2xl font-black italic text-amber-400 mt-1">
                {suspendedCount}
              </div>
              <span className="text-[10px] text-amber-300 font-bold mt-1 block">Temporary Holds</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 block">
                Banned Accounts
              </span>
              <div className="text-2xl font-black italic text-rose-400 mt-1">
                {bannedCount}
              </div>
              <span className="text-[10px] text-rose-300 font-bold mt-1 block">Access Revoked</span>
            </div>
          </div>

          {/* Search & Status Filters */}
          <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-indigo-300 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search user by name, @handle, email, sport, or role..."
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs font-bold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div className="flex items-center space-x-1 bg-indigo-950 p-1 rounded-xl border border-white/10">
                <button
                  onClick={() => setUserStatusFilter('all')}
                  className={`px-3 py-1 rounded-lg text-xs font-black italic uppercase transition ${
                    userStatusFilter === 'all' ? 'bg-indigo-700 text-white' : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  All ({athletes.length})
                </button>
                <button
                  onClick={() => setUserStatusFilter('active')}
                  className={`px-3 py-1 rounded-lg text-xs font-black italic uppercase transition ${
                    userStatusFilter === 'active' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  Active ({activeCount})
                </button>
                <button
                  onClick={() => setUserStatusFilter('suspended')}
                  className={`px-3 py-1 rounded-lg text-xs font-black italic uppercase transition ${
                    userStatusFilter === 'suspended' ? 'bg-amber-400 text-black' : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  Suspended ({suspendedCount})
                </button>
                <button
                  onClick={() => setUserStatusFilter('banned')}
                  className={`px-3 py-1 rounded-lg text-xs font-black italic uppercase transition ${
                    userStatusFilter === 'banned' ? 'bg-rose-500 text-white' : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  Banned ({bannedCount})
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-indigo-300/60 uppercase font-black italic text-[10px]">
                    <th className="pb-3 px-2">Athlete Profile</th>
                    <th className="pb-3 px-2">Role / Sport</th>
                    <th className="pb-3 px-2">Status</th>
                    <th className="pb-3 px-2">Stats / Level</th>
                    <th className="pb-3 px-2 text-right">Admin Powers & Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-indigo-300/60 font-semibold">
                        No user profiles match your search criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((athlete) => {
                      const status = athlete.accountStatus || 'active';
                      return (
                        <tr key={athlete.id} className="hover:bg-indigo-950/40 transition">
                          {/* Profile */}
                          <td className="py-3 px-2">
                            <div className="flex items-center space-x-3">
                              <img
                                src={athlete.avatar}
                                alt={athlete.name}
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10 shrink-0"
                                referrerPolicy="no-referrer"
                              />
                              <div>
                                <div className="font-extrabold text-white">{athlete.name}</div>
                                <div className="text-[10px] text-indigo-300/70 font-mono">
                                  {athlete.handle} • {athlete.email}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Role / Sport */}
                          <td className="py-3 px-2">
                            <span className="px-2 py-0.5 rounded-md bg-indigo-950 text-indigo-200 border border-white/10 font-black italic uppercase text-[10px] block w-max">
                              {athlete.role.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] text-lime-400 font-bold uppercase italic mt-0.5 block">
                              {athlete.primarySport}
                            </span>
                          </td>

                          {/* Status & Subscription Tier */}
                          <td className="py-3 px-2">
                            <div className="flex flex-col gap-1">
                              {status === 'active' && (
                                <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/30 font-black uppercase text-[10px] inline-flex items-center space-x-1 w-max">
                                  <CheckCircle2 className="w-3 h-3 text-lime-400" />
                                  <span>ACTIVE</span>
                                </span>
                              )}
                              {status === 'suspended' && (
                                <span className="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30 font-black uppercase text-[10px] inline-flex items-center space-x-1 w-max">
                                  <AlertTriangle className="w-3 h-3 text-amber-400" />
                                  <span>SUSPENDED</span>
                                </span>
                              )}
                              {status === 'banned' && (
                                <span className="px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 font-black uppercase text-[10px] inline-flex items-center space-x-1 w-max">
                                  <UserX className="w-3 h-3 text-rose-400" />
                                  <span>BANNED</span>
                                </span>
                              )}

                              {(athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro') ? (
                                <span className="px-2.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 text-black border border-amber-300 font-black uppercase text-[10px] inline-flex items-center space-x-1 shadow-md w-max">
                                  <Crown className="w-3 h-3 fill-black" />
                                  <span>PRO TIER</span>
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full bg-indigo-950/80 text-indigo-300/80 border border-white/10 font-black uppercase text-[10px] inline-flex items-center space-x-1 w-max">
                                  <Users className="w-3 h-3 text-indigo-400" />
                                  <span>FREE TIER</span>
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Stats */}
                          <td className="py-3 px-2">
                            <div className="font-black italic text-lime-400 text-xs flex items-center">
                              <span>LVL {athlete.level} ({athlete.levelTitle})</span>
                            </div>
                            <div className="text-[10px] text-indigo-300/70 font-semibold">
                              {athlete.winCount}W - {athlete.lossCount}L
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-2 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {/* Quick PRO Status Toggle Switch */}
                              <div
                                className="flex items-center space-x-1.5 bg-indigo-950/80 px-2 py-1 rounded-xl border border-white/10"
                                title={athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro' ? "PRO Active - Click to toggle OFF" : "FREE Tier - Click to toggle ON"}
                              >
                                <span className="text-[10px] font-extrabold uppercase text-amber-300 font-mono hidden sm:inline">
                                  PRO
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const isPro = athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro';
                                    if (isPro) {
                                      setRevokeProModalUser(athlete);
                                    } else {
                                      setGrantProModalUser(athlete);
                                    }
                                  }}
                                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 ease-in-out focus:outline-none ${
                                    athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro'
                                      ? 'bg-gradient-to-r from-amber-400 to-yellow-300 border-amber-300 shadow-md shadow-amber-400/30'
                                      : 'bg-indigo-900 border-white/20 hover:border-amber-400/50'
                                  }`}
                                  aria-label="Toggle PRO Subscription Status"
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-slate-950 shadow ring-0 transition duration-200 ease-in-out flex items-center justify-center ${
                                      athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro'
                                        ? 'translate-x-5 bg-black text-amber-400'
                                        : 'translate-x-0 text-slate-500'
                                    }`}
                                  >
                                    <Crown className={`w-3 h-3 ${athlete.isPro || athlete.subscriptionTier === 'pro' || athlete.role === 'playground_pro' ? 'fill-amber-400 text-amber-400' : 'text-slate-500'}`} />
                                  </span>
                                </button>
                              </div>

                              {/* Scout Access Validation Toggle */}
                              <button
                                type="button"
                                onClick={() => {
                                  const isValidated = !!athlete.isScoutValidated;
                                  if (onValidateScoutAccess) {
                                    onValidateScoutAccess(athlete.id, !isValidated);
                                    setActionNotice(isValidated ? `Revoked Scout access for ${athlete.name}` : `Validated account & granted Scout access for ${athlete.name}`);
                                    addAuditLog(`${isValidated ? 'Revoked' : 'Validated'} Scout access for ${athlete.name} (${athlete.handle})`, 'reset');
                                  }
                                }}
                                className={`px-2.5 py-1.5 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 border transition ${
                                  athlete.isScoutValidated
                                    ? 'bg-purple-600/30 text-purple-200 border-purple-400/50 hover:bg-purple-600/50'
                                    : 'bg-indigo-950 text-indigo-300 border-white/10 hover:border-purple-400/50'
                                }`}
                                title={athlete.isScoutValidated ? "Scout Validated - Click to Revoke" : "Unvalidated - Click to Validate & Grant Scout Access"}
                              >
                                <Eye className={`w-3 h-3 ${athlete.isScoutValidated ? 'text-purple-300' : 'text-indigo-400'}`} />
                                <span className="hidden xl:inline">{athlete.isScoutValidated ? 'Scout Validated ✓' : 'Validate Scout'}</span>
                              </button>

                              {/* Reset Stats */}
                              <button
                                onClick={() => {
                                  if (confirm(`Are you sure you want to RESET all match stats, records, and level XP for athlete "${athlete.name}" to 0?`)) {
                                    if (onResetUserStats) {
                                      onResetUserStats(athlete.id);
                                    }
                                    setActionNotice(`Reset stats & XP for athlete ${athlete.name}`);
                                    addAuditLog(`Reset stats and XP for user ${athlete.name} (${athlete.handle})`, 'reset');
                                  }
                                }}
                                className="px-2.5 py-1.5 bg-indigo-950 hover:bg-rose-500/20 hover:text-rose-300 text-indigo-200 border border-white/10 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                title="Reset User Match Stats & Level XP"
                              >
                                <RotateCcw className="w-3 h-3 text-rose-400" />
                                <span className="hidden xl:inline">Reset Stats</span>
                              </button>

                              {/* Reset Password */}
                              <button
                                onClick={() => {
                                  const temp = onResetUserPassword(athlete.id);
                                  setTempPasswordModal({ userName: athlete.name, tempPass: temp });
                                  addAuditLog(`Reset password for user ${athlete.name} (${athlete.handle})`, 'reset');
                                }}
                                className="px-2.5 py-1.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                title="Reset User Password"
                              >
                                <Key className="w-3 h-3 text-lime-400" />
                                <span className="hidden xl:inline">Reset Pass</span>
                              </button>

                              {/* Suspend / Reactivate */}
                              {status === 'suspended' ? (
                                <button
                                  onClick={() => {
                                    onUpdateUserStatus(athlete.id, 'active');
                                    setActionNotice(`Reactivated user ${athlete.name}`);
                                    addAuditLog(`Reactivated user ${athlete.name}`, 'suspend');
                                  }}
                                  className="px-2.5 py-1.5 bg-lime-400/20 hover:bg-lime-400 hover:text-black text-lime-300 border border-lime-400/40 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                >
                                  <UserCheck className="w-3 h-3" />
                                  <span>Unsuspend</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => setSuspendModalUser(athlete)}
                                  className="px-2.5 py-1.5 bg-amber-400/20 hover:bg-amber-400 hover:text-black text-amber-300 border border-amber-400/40 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                  title="Suspend user account"
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  <span>Suspend</span>
                                </button>
                              )}

                              {/* Ban / Unban */}
                              {status === 'banned' ? (
                                <button
                                  onClick={() => {
                                    onUpdateUserStatus(athlete.id, 'active');
                                    setActionNotice(`Unbanned user ${athlete.name}`);
                                    addAuditLog(`Unbanned user ${athlete.name}`, 'ban');
                                  }}
                                  className="px-2.5 py-1.5 bg-lime-400/20 hover:bg-lime-400 hover:text-black text-lime-300 border border-lime-400/40 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                >
                                  <UserCheck className="w-3 h-3" />
                                  <span>Unban</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    if (confirm(`Are you sure you want to BAN user ${athlete.name}? They will lose access to the platform.`)) {
                                      onUpdateUserStatus(athlete.id, 'banned', 'Admin platform policy violation ban');
                                      setActionNotice(`Banned user ${athlete.name}`);
                                      addAuditLog(`Banned user ${athlete.name} (${athlete.handle})`, 'ban');
                                    }
                                  }}
                                  className="px-2.5 py-1.5 bg-rose-500/20 hover:bg-rose-500 hover:text-white text-rose-300 border border-rose-500/40 rounded-xl font-black italic text-[10px] uppercase flex items-center space-x-1 transition"
                                  title="Ban user account"
                                >
                                  <UserX className="w-3 h-3" />
                                  <span>Ban</span>
                                </button>
                              )}

                              {/* Delete Account */}
                              <button
                                onClick={() => {
                                  if (confirm(`CRITICAL: Permanently DELETE athlete account "${athlete.name}"? This action cannot be undone.`)) {
                                    onDeleteUser(athlete.id);
                                    setActionNotice(`Deleted athlete account "${athlete.name}"`);
                                    addAuditLog(`Permanently deleted account ${athlete.name}`, 'delete');
                                  }
                                }}
                                className="p-1.5 bg-rose-950/60 hover:bg-rose-600 text-rose-400 hover:text-white rounded-xl border border-rose-500/30 transition"
                                title="Delete User Account"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION: PAYOUT & BANK DASHBOARD ================= */}
      {adminSection === 'payouts' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Revenue Overview Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black italic uppercase text-indigo-300/70 tracking-wider">
                  Total Gross Revenue
                </span>
                <div className="p-2 bg-emerald-400/20 text-emerald-400 rounded-xl">
                  <DollarSign className="w-4 h-4" />
                </div>
              </div>
              <div className="text-3xl font-black italic text-white mt-2">$14,820.00</div>
              <span className="text-[11px] text-emerald-400 font-bold mt-1 block flex items-center gap-1">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>+24.8% from PRO Subscriptions</span>
              </span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black italic uppercase text-indigo-300/70 tracking-wider">
                  Available Payout Balance
                </span>
                <div className="p-2 bg-lime-400/20 text-lime-400 rounded-xl">
                  <Building2 className="w-4 h-4" />
                </div>
              </div>
              <div className="text-3xl font-black italic text-lime-400 mt-2">$12,450.00</div>
              <span className="text-[11px] text-indigo-200/80 font-bold mt-1 block">Ready for Direct Bank Transfer</span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black italic uppercase text-indigo-300/70 tracking-wider">
                  Stripe Connect Volume
                </span>
                <div className="p-2 bg-amber-400/20 text-amber-400 rounded-xl">
                  <CreditCard className="w-4 h-4" />
                </div>
              </div>
              <div className="text-3xl font-black italic text-white mt-2">$9,840.00</div>
              <span className="text-[11px] text-amber-300 font-mono mt-1 block">Credit Cards, Apple & Google Pay</span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black italic uppercase text-indigo-300/70 tracking-wider">
                  PayPal Payout Volume
                </span>
                <div className="p-2 bg-blue-400/20 text-blue-400 rounded-xl">
                  <span className="font-black italic text-sm">P</span>
                </div>
              </div>
              <div className="text-3xl font-black italic text-white mt-2">$4,980.00</div>
              <span className="text-[11px] text-blue-300 font-mono mt-1 block">PayPal Express Subscriptions</span>
            </div>
          </div>

          {/* Stripe API & Managed Payments Diagnostic Panel */}
          <div className="bg-indigo-950 border-2 border-lime-400/40 p-6 rounded-[2.5rem] shadow-2xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-2xl bg-lime-400/20 border border-lime-400/40 text-lime-400 flex items-center justify-center shrink-0">
                  <Terminal className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-base font-black italic uppercase text-white">
                      Stripe API & Checkout Diagnostics
                    </h3>
                    <span className="px-2 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[9px] font-black uppercase font-mono">
                      MANAGED PAYMENTS ENABLED
                    </span>
                  </div>
                  <p className="text-xs text-indigo-200/70 font-medium mt-0.5">
                    Test live & simulated Checkout Session creation with <code className="text-lime-300 font-mono">managed_payments[enabled]: true</code> and API version <code className="text-amber-300 font-mono">2026-02-25.preview</code>.
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  id="admin-stripe-diagnostic-btn"
                  disabled={isStripeDiagnosticRunning}
                  onClick={handleRunStripeDiagnostic}
                  className="px-5 py-2.5 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-2 disabled:opacity-50"
                >
                  {isStripeDiagnosticRunning ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Creating Diagnostic Session...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-black" />
                      <span>Run Mock 'Create Checkout Session' Call</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Target Test Account Meta */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs bg-indigo-900/40 p-3 rounded-2xl border border-white/5 font-mono">
              <div>
                <span className="text-indigo-300/60 block text-[10px] uppercase">Active Diagnostic User</span>
                <span className="text-white font-bold">{currentUser?.name || athletes[0]?.name || 'Superadmin Tester'}</span>
              </div>
              <div>
                <span className="text-indigo-300/60 block text-[10px] uppercase">User ID & Email</span>
                <span className="text-amber-300 truncate block">{currentUser?.id || 'admin_test_user'} ({currentUser?.email || 'admin@playgroundleague.pro'})</span>
              </div>
              <div>
                <span className="text-indigo-300/60 block text-[10px] uppercase">Stripe Blueprint Headers</span>
                <span className="text-lime-400">stripe-version: 2026-02-25.preview</span>
              </div>
            </div>

            {/* Diagnostic Results Card (when executed) */}
            {stripeDiagnosticResult && (
              <div className="bg-indigo-900/80 border border-lime-400/30 p-4 rounded-2xl space-y-3 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2.5">
                  <div className="flex items-center space-x-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-lime-400 animate-pulse"></span>
                    <span className="text-xs font-black uppercase text-lime-400">
                      Diagnostic Session Created Successfully (Logged to Console ✓)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-mono text-indigo-300">
                      Tested at: {stripeDiagnosticResult.timestamp}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowDiagnosticJson(!showDiagnosticJson)}
                      className="px-2.5 py-1 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-lg text-[10px] font-mono transition"
                    >
                      {showDiagnosticJson ? 'Hide Raw JSON' : 'Inspect JSON Response'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs font-mono">
                  <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-400 uppercase block">Session ID</span>
                    <span className="text-amber-300 font-bold break-all">{stripeDiagnosticResult.sessionId}</span>
                  </div>
                  <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-400 uppercase block">Managed Payments</span>
                    <span className="text-lime-300 font-bold">managed_payments[enabled]: true ✓</span>
                  </div>
                  <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-400 uppercase block">API Version</span>
                    <span className="text-indigo-200 font-bold">{stripeDiagnosticResult.stripeVersion}</span>
                  </div>
                  <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-400 uppercase block">Checkout URL</span>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <span className="text-white truncate max-w-[140px] text-[11px]">{stripeDiagnosticResult.url || 'N/A'}</span>
                      {stripeDiagnosticResult.url && (
                        <a
                          href={stripeDiagnosticResult.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-lime-400 hover:text-lime-300 inline-flex items-center"
                          title="Open Checkout Preview"
                        >
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {showDiagnosticJson && (
                  <div className="mt-3 p-3 bg-black/80 rounded-xl border border-white/10 text-[11px] font-mono text-lime-300 overflow-x-auto max-h-48">
                    <pre>{JSON.stringify(stripeDiagnosticResult.payload, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Two-Column Grid: Bank Details & Revenue Withdrawal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Card: Platform Owner Bank Configuration */}
            <div className="bg-indigo-900/70 p-6 rounded-[2.5rem] border border-white/10 shadow-2xl space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center space-x-2 text-amber-400">
                  <Building2 className="w-5 h-5" />
                  <h3 className="text-lg font-black italic uppercase text-white">
                    Platform Owner Bank Account (Revenue Payouts)
                  </h3>
                </div>
                <span className="px-2.5 py-0.5 bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 rounded-full font-mono text-[10px] font-bold">
                  Stripe Connect Express Connected ✓
                </span>
              </div>

              <p className="text-xs text-indigo-200/80 font-medium">
                Configure your corporate or personal bank account details to automatically receive funds from athlete PRO subscriptions, tournament fees, and court reservations.
              </p>

              <div className="space-y-3 font-mono text-xs">
                <div>
                  <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={withdrawalBankName}
                    onChange={(e) => setWithdrawalBankName(e.target.value)}
                    className="w-full bg-indigo-950 border border-white/15 rounded-xl px-3.5 py-2.5 text-white font-bold focus:outline-none focus:border-amber-400"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">Account Holder Name</label>
                  <input
                    type="text"
                    value={withdrawalAccountHolder}
                    onChange={(e) => setWithdrawalAccountHolder(e.target.value)}
                    className="w-full bg-indigo-950 border border-white/15 rounded-xl px-3.5 py-2.5 text-white font-bold focus:outline-none focus:border-amber-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">Account Number / IBAN</label>
                    <input
                      type="text"
                      value={withdrawalAccountNumber}
                      onChange={(e) => setWithdrawalAccountNumber(e.target.value)}
                      className="w-full bg-indigo-950 border border-white/15 rounded-xl px-3.5 py-2.5 text-amber-300 font-bold focus:outline-none focus:border-amber-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">Routing Number (ABA)</label>
                    <input
                      type="text"
                      value={withdrawalRoutingNumber}
                      onChange={(e) => setWithdrawalRoutingNumber(e.target.value)}
                      className="w-full bg-indigo-950 border border-white/15 rounded-xl px-3.5 py-2.5 text-amber-300 font-bold focus:outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">SWIFT / IBAN Code</label>
                  <input
                    type="text"
                    value={withdrawalSwiftIban}
                    onChange={(e) => setWithdrawalSwiftIban(e.target.value)}
                    className="w-full bg-indigo-950 border border-white/15 rounded-xl px-3.5 py-2.5 text-white font-bold focus:outline-none focus:border-amber-400"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setActionNotice('Platform Bank Receiving Details updated successfully.');
                    addAuditLog('Updated platform owner bank payout receiving configuration', 'park');
                  }}
                  className="w-full py-2.5 bg-indigo-800 hover:bg-indigo-700 text-white font-black italic uppercase rounded-xl border border-white/10 transition text-xs flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-4 h-4 text-lime-400" />
                  <span>Save Bank Payout Receiving Settings</span>
                </button>
              </div>
            </div>

            {/* Right Card: Revenue Withdrawal Action */}
            <div className="bg-indigo-900/70 p-6 rounded-[2.5rem] border border-white/10 shadow-2xl space-y-5 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center space-x-2 text-lime-400">
                    <DollarSign className="w-5 h-5" />
                    <h3 className="text-lg font-black italic uppercase text-white">
                      Withdraw Platform Revenue
                    </h3>
                  </div>
                  <span className="px-2.5 py-0.5 bg-lime-400 text-black font-mono text-[10px] font-black uppercase rounded">
                    Instant Bank Wire Available
                  </span>
                </div>

                <div className="p-4 bg-indigo-950/90 rounded-2xl border border-lime-400/30 space-y-2">
                  <span className="text-[10px] font-black uppercase text-indigo-300 block">Current Available Balance</span>
                  <div className="text-3xl font-black italic text-lime-400">$12,450.00 USD</div>
                  <p className="text-[11px] text-indigo-200/80">
                    Funds will be disbursed via Stripe Connect Wire or PayPal Payout directly to <strong>{withdrawalBankName}</strong> (*{withdrawalAccountNumber.slice(-4)}).
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-black uppercase text-indigo-200">
                    Enter Withdrawal Amount ($USD):
                  </label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-lime-400 absolute left-3.5 top-3" />
                    <input
                      type="number"
                      value={withdrawalAmountInput}
                      onChange={(e) => setWithdrawalAmountInput(e.target.value)}
                      placeholder="Amount to withdraw..."
                      className="w-full bg-indigo-950 border border-white/20 rounded-2xl pl-10 pr-4 py-3 text-lg font-black font-mono text-white focus:outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <button
                  type="button"
                  disabled={isProcessingWithdrawal}
                  onClick={() => {
                    const amountToWithdraw = parseFloat(withdrawalAmountInput) || 0;
                    if (amountToWithdraw <= 0) return;

                    setIsProcessingWithdrawal(true);
                    setTimeout(() => {
                      setIsProcessingWithdrawal(false);
                      const newWithdrawal = {
                        id: `w-${Date.now().toString().slice(-4)}`,
                        date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
                        amount: `$${amountToWithdraw.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                        bank: `${withdrawalBankName.slice(0, 15)} (*${withdrawalAccountNumber.slice(-4)})`,
                        method: 'Stripe Connect Express Wire',
                        status: 'Completed',
                      };
                      setPayoutLogs([newWithdrawal, ...payoutLogs]);
                      setActionNotice(`Successfully disbursed $${amountToWithdraw.toLocaleString()} to ${withdrawalBankName}!`);
                      addAuditLog(`Disbursed revenue withdrawal of $${amountToWithdraw.toLocaleString()} to ${withdrawalBankName}`, 'park');
                    }, 1200);
                  }}
                  className="w-full py-3.5 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2"
                >
                  {isProcessingWithdrawal ? (
                    <span className="animate-pulse">Processing Direct Bank Disbursal...</span>
                  ) : (
                    <>
                      <ArrowUpRight className="w-5 h-5" />
                      <span>Disburse ${withdrawalAmountInput} to Bank Account</span>
                    </>
                  )}
                </button>

                <div className="text-[11px] text-indigo-300/60 font-mono text-center">
                  Zero transfer fee • 256-Bit FDIC compliant clearing house
                </div>
              </div>
            </div>
          </div>

          {/* Stripe Hosted Checkout Diagnostic & API Verification Card */}
          <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-amber-400/40 shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-amber-400/20 text-amber-300 rounded-2xl border border-amber-400/40">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black italic uppercase text-white text-lg flex items-center space-x-2">
                    <span>Stripe Checkout API Diagnostics</span>
                    <span className="px-2 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-mono font-bold">
                      LIVE MODE READY
                    </span>
                  </h3>
                  <p className="text-xs text-indigo-200/80 font-medium">
                    Test server-side Checkout Session generation without redirecting away from the admin portal.
                  </p>
                </div>
              </div>

              <button
                type="button"
                id="admin-stripe-diagnostic-btn"
                disabled={isStripeDiagnosticRunning}
                onClick={handleRunStripeDiagnostic}
                className="px-4 py-3 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-2 shrink-0 disabled:opacity-50"
              >
                {isStripeDiagnosticRunning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Executing Diagnostic...</span>
                  </>
                ) : (
                  <>
                    <Terminal className="w-4 h-4" />
                    <span>Run Checkout Diagnostic</span>
                  </>
                )}
              </button>
            </div>

            {stripeDiagnosticResult && (
              <div className={`p-4 rounded-2xl border ${stripeDiagnosticResult.success ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-200' : 'bg-rose-950/60 border-rose-500/40 text-rose-200'} space-y-3 animate-fadeIn text-xs`}>
                <div className="flex items-center justify-between font-bold">
                  <span className="flex items-center space-x-2">
                    {stripeDiagnosticResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-rose-400" />}
                    <span>{stripeDiagnosticResult.success ? 'Diagnostic Test Passed: Checkout Session Created' : 'Diagnostic Test Failed'}</span>
                  </span>
                  <span className="font-mono text-[11px] opacity-75">{stripeDiagnosticResult.timestamp}</span>
                </div>

                {stripeDiagnosticResult.sessionId && (
                  <div className="font-mono text-[11px] bg-black/40 p-3 rounded-xl border border-white/10 space-y-1">
                    <div><strong className="text-amber-300">Session ID:</strong> {stripeDiagnosticResult.sessionId}</div>
                    {(stripeDiagnosticResult.checkoutUrl || stripeDiagnosticResult.url) && (
                      <div className="truncate"><strong className="text-lime-300">Redirect URL:</strong> {stripeDiagnosticResult.checkoutUrl || stripeDiagnosticResult.url}</div>
                    )}
                    <div><strong className="text-indigo-300">Mode:</strong> {stripeDiagnosticResult.isSimulated ? 'Fallback Simulation' : 'Live / Sandbox Stripe API Endpoint'}</div>
                  </div>
                )}

                {stripeDiagnosticResult.error && (
                  <div className="font-mono text-[11px] bg-black/40 p-3 rounded-xl border border-rose-500/40 text-rose-300">
                    {stripeDiagnosticResult.error}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Athlete PRO Transactions Table */}
          <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black italic uppercase text-white text-lg flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                <span>Recent Athlete PRO Upgrade Transactions</span>
              </h3>
              <span className="text-xs font-mono text-indigo-300/70">Multi-source Payment Ledger</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-black uppercase text-indigo-300/60 font-mono">
                    <th className="py-2.5 px-3">Athlete</th>
                    <th className="py-2.5 px-3">Plan Selected</th>
                    <th className="py-2.5 px-3">Payment Source</th>
                    <th className="py-2.5 px-3">Receipt ID</th>
                    <th className="py-2.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {transactionHistory.map((tx) => (
                    <tr key={tx.id} className="hover:bg-indigo-950/60 transition">
                      <td className="py-3 px-3 font-bold text-white">{tx.athlete}</td>
                      <td className="py-3 px-3 text-indigo-200">{tx.plan}</td>
                      <td className="py-3 px-3 font-mono text-amber-300">{tx.source}</td>
                      <td className="py-3 px-3 font-mono text-indigo-300">{tx.receipt}</td>
                      <td className="py-3 px-3">
                        <span className="px-2 py-0.5 rounded bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 text-[10px] font-bold uppercase font-mono">
                          Verified Paid ✓
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Withdrawal History Log Table */}
          <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black italic uppercase text-white text-lg flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-lime-400" />
                <span>Bank Disbursal Withdrawal History</span>
              </h3>
              <span className="text-xs font-mono text-indigo-300/70">{payoutLogs.length} Withdrawals</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-black uppercase text-indigo-300/60 font-mono">
                    <th className="py-2.5 px-3">Disbursal ID</th>
                    <th className="py-2.5 px-3">Date</th>
                    <th className="py-2.5 px-3">Amount</th>
                    <th className="py-2.5 px-3">Destination Bank</th>
                    <th className="py-2.5 px-3">Method</th>
                    <th className="py-2.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {payoutLogs.map((p) => (
                    <tr key={p.id} className="hover:bg-indigo-950/60 transition">
                      <td className="py-3 px-3 font-mono text-amber-300 font-bold">{p.id}</td>
                      <td className="py-3 px-3 text-indigo-200">{p.date}</td>
                      <td className="py-3 px-3 font-black text-lime-400 text-sm">{p.amount}</td>
                      <td className="py-3 px-3 font-mono text-white">{p.bank}</td>
                      <td className="py-3 px-3 font-mono text-indigo-300">{p.method}</td>
                      <td className="py-3 px-3">
                        <span className="px-2 py-0.5 rounded bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[10px] font-bold uppercase font-mono">
                          {p.status} ✓
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION 2: METRICS & CONCURRENCY ================= */}
      {adminSection === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 tracking-wider block">
                Total Registered Athletes
              </span>
              <div className="text-3xl font-black italic text-white mt-1">
                {safeAnalytics.totalUsers.toLocaleString()}
              </div>
              <span className="text-xs text-lime-400 font-bold mt-1 block">+18% this month</span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 tracking-wider block">
                Active Today
              </span>
              <div className="text-3xl font-black italic text-white mt-1">
                {safeAnalytics.activeToday.toLocaleString()}
              </div>
              <span className="text-xs text-rose-400 font-bold mt-1 block">Peak concurrency 4,800</span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 tracking-wider block">
                Pickup Games Hosted
              </span>
              <div className="text-3xl font-black italic text-white mt-1">
                {safeAnalytics.gamesHostedThisWeek}
              </div>
              <span className="text-xs text-lime-400 font-bold mt-1 block">+32 hosted games</span>
            </div>

            <div className="bg-indigo-900/60 p-5 rounded-[2rem] border border-white/10 shadow-xl">
              <span className="text-[10px] font-black italic uppercase text-indigo-300/60 tracking-wider block">
                Offline Sync Rate
              </span>
              <div className="text-3xl font-black italic text-white mt-1">
                {safeAnalytics.syncSuccessRate}%
              </div>
              <span className="text-xs text-lime-400 font-bold mt-1 block">Auto-conflict resolution</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-black italic uppercase text-white text-base">
                    Real-Time User Concurrency & Latency
                  </h3>
                  <p className="text-xs text-indigo-200/70 font-semibold">
                    Throughput across peak afternoon/evening runs
                  </p>
                </div>
                <span className="text-xs font-black italic uppercase text-lime-400 bg-lime-400/20 px-2.5 py-1 rounded-xl border border-lime-400/30">
                  Avg 28ms Latency
                </span>
              </div>

              <div className="h-64 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={safeAnalytics.concurrencyData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#C7D2FE" fontSize={12} />
                    <YAxis stroke="#C7D2FE" fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1B4B', borderColor: 'rgba(255,255,255,0.2)', borderRadius: '16px', color: '#fff' }} />
                    <Line type="monotone" dataKey="users" name="Active Users" stroke="#A3E635" strokeWidth={3} />
                    <Line type="monotone" dataKey="latencyMs" name="DB Latency (ms)" stroke="#F43F5E" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-black italic uppercase text-white text-base">
                    Court Traffic & Geo-Fence Check-Ins
                  </h3>
                  <p className="text-xs text-indigo-200/70 font-semibold">
                    Most visited playground courts
                  </p>
                </div>
              </div>

              <div className="h-64 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={safeAnalytics.topCourts} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" stroke="#C7D2FE" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#C7D2FE" fontSize={10} width={120} />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1B4B', borderColor: 'rgba(255,255,255,0.2)', borderRadius: '16px', color: '#fff' }} />
                    <Bar dataKey="visits" name="Visits Today" fill="#A3E635" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION: PLATFORM USAGE METRICS ================= */}
      {adminSection === 'platform_metrics' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Platform Metrics Header */}
          <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3.5">
                <div className="w-11 h-11 rounded-2xl bg-lime-400/20 border border-lime-400/40 text-lime-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-black italic uppercase text-white text-xl">
                      Platform Usage Metrics & Daily Active Athlete Trends
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase font-mono">
                      RECHARTS VISUALIZATION
                    </span>
                  </div>
                  <p className="text-xs text-indigo-200/70 mt-0.5 font-medium">
                    Comprehensive 30-day historical activity analytics tracking daily active athletes (DAA), hosted pickup runs, and pro subscription conversions.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  const headers = ["Date", "Active Athletes", "Pickup Games Hosted", "PRO Upgrades"];
                  const rows = dailyActiveAthleteData.map(d => [d.date, d.activeAthletes, d.pickupGames, d.proUpgrades]);
                  const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
                  const encodedUri = encodeURI(csvContent);
                  const link = document.createElement("a");
                  link.setAttribute("href", encodedUri);
                  link.setAttribute("download", `daily_active_athletes_30days_${Date.now()}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  triggerAuthToast('Metrics Exported', '30-Day Daily Active Athlete CSV Downloaded');
                }}
                className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-900 border border-white/15 text-lime-300 font-black italic uppercase text-xs rounded-2xl shadow-md transition flex items-center space-x-2 shrink-0"
              >
                <Download className="w-4 h-4" />
                <span>Export Daily Usage CSV</span>
              </button>
            </div>

            {/* Metric Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-indigo-950/80 border border-white/10 rounded-2xl">
                <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">30-Day Peak DAA</div>
                <div className="text-2xl font-black italic text-lime-400 mt-1">
                  {Math.max(...dailyActiveAthleteData.map(d => d.activeAthletes)).toLocaleString()} Athletes
                </div>
                <div className="text-[10px] text-lime-300/80 font-bold mt-1">⚡ Highest Single-Day Run</div>
              </div>

              <div className="p-4 bg-indigo-950/80 border border-white/10 rounded-2xl">
                <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">30-Day Average DAA</div>
                <div className="text-2xl font-black italic text-white mt-1">
                  {Math.round(dailyActiveAthleteData.reduce((acc, d) => acc + d.activeAthletes, 0) / dailyActiveAthleteData.length).toLocaleString()} Athletes/Day
                </div>
                <div className="text-[10px] text-indigo-300/70 font-medium mt-1">📈 Consistent High Concurrency</div>
              </div>

              <div className="p-4 bg-indigo-950/80 border border-white/10 rounded-2xl">
                <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">30-Day Total Pickup Runs</div>
                <div className="text-2xl font-black italic text-amber-300 mt-1">
                  {dailyActiveAthleteData.reduce((acc, d) => acc + d.pickupGames, 0).toLocaleString()} Games
                </div>
                <div className="text-[10px] text-amber-300/80 font-bold mt-1">🏀 Across Basketball & Volleyball</div>
              </div>

              <div className="p-4 bg-indigo-950/80 border border-white/10 rounded-2xl">
                <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">Active Athletes Today</div>
                <div className="text-2xl font-black italic text-emerald-300 mt-1">
                  {dailyActiveAthleteData[dailyActiveAthleteData.length - 1].activeAthletes.toLocaleString()} Live
                </div>
                <div className="text-[10px] text-emerald-300/80 font-bold mt-1">🔥 Up +18% vs Last Month</div>
              </div>
            </div>

            {/* Controls Bar for Recharts Bar Chart */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
              <div className="flex items-center space-x-1.5 p-1 bg-indigo-950 rounded-xl border border-white/10 text-xs font-bold">
                <span className="text-[10px] font-mono text-indigo-300 uppercase px-2">Metric:</span>
                {[
                  { key: 'activeAthletes', label: 'Daily Active Athletes' },
                  { key: 'pickupGames', label: 'Pickup Games' },
                  { key: 'proUpgrades', label: 'PRO Upgrades' }
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setPlatformMetricsMetric(m.key as any)}
                    className={`px-3 py-1.5 rounded-lg transition text-[11px] font-black uppercase italic ${
                      platformMetricsMetric === m.key ? 'bg-lime-400 text-black shadow-sm' : 'text-indigo-300 hover:text-white'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-1.5 p-1 bg-indigo-950 rounded-xl border border-white/10 text-xs font-bold">
                <span className="text-[10px] font-mono text-indigo-300 uppercase px-2">Timeframe:</span>
                {[30, 14, 7].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPlatformMetricsDays(num as any)}
                    className={`px-3 py-1.5 rounded-lg transition text-[11px] font-black font-mono ${
                      platformMetricsDays === num ? 'bg-amber-400 text-black shadow-sm' : 'text-indigo-300 hover:text-white'
                    }`}
                  >
                    {num} Days
                  </button>
                ))}
              </div>
            </div>

            {/* Recharts Bar Chart Container */}
            <div className="p-5 bg-indigo-950/90 rounded-3xl border border-white/10 shadow-inner space-y-3">
              <div className="flex items-center justify-between text-xs font-bold text-indigo-200 border-b border-white/10 pb-3">
                <span className="flex items-center space-x-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-lime-400 animate-pulse" />
                  <span className="uppercase italic tracking-wider">
                    {platformMetricsMetric === 'activeAthletes' ? 'Daily Active Athletes Count' : platformMetricsMetric === 'pickupGames' ? 'Daily Hosted Pickup Games' : 'Daily PRO Upgrades'} ({platformMetricsDays} Days)
                  </span>
                </span>
                <span className="font-mono text-[10px] text-indigo-300/60">
                  Showing last {platformMetricsDays} days of verified telemetry
                </span>
              </div>

              <div className="w-full h-[320px] pt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailyActiveAthleteData.slice(-platformMetricsDays)} margin={{ top: 10, right: 10, left: -20, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#a5b4fc"
                      tick={{ fill: '#c7d2fe', fontSize: 10, fontWeight: 700 }}
                      interval={platformMetricsDays === 30 ? 2 : 0}
                      angle={-30}
                      textAnchor="end"
                    />
                    <YAxis
                      stroke="#a5b4fc"
                      tick={{ fill: '#c7d2fe', fontSize: 10, fontWeight: 700 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#090d16',
                        borderColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '1rem',
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
                      }}
                      itemStyle={{ color: '#a3e635' }}
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                    />
                    <Bar
                      dataKey={platformMetricsMetric}
                      name={platformMetricsMetric === 'activeAthletes' ? 'Active Athletes' : platformMetricsMetric === 'pickupGames' ? 'Pickup Games' : 'PRO Upgrades'}
                      fill={platformMetricsMetric === 'activeAthletes' ? '#a3e635' : platformMetricsMetric === 'pickupGames' ? '#f59e0b' : '#38bdf8'}
                      radius={[6, 6, 0, 0]}
                    />
                    <Line
                      type="monotone"
                      dataKey="rollingAvg7Day"
                      name="7-Day Rolling Avg (DAA)"
                      stroke="#f59e0b"
                      strokeWidth={3}
                      strokeDasharray="6 6"
                      dot={{ fill: '#f59e0b', r: 3 }}
                      activeDot={{ r: 6, fill: '#fbbf24', stroke: '#ffffff', strokeWidth: 2 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-wrap items-center justify-between text-[11px] font-mono text-indigo-300/70 pt-2 border-t border-white/10">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center space-x-1.5">
                    <span className="w-3 h-3 rounded bg-lime-400" />
                    <span>{platformMetricsMetric === 'activeAthletes' ? 'Daily Active Athletes' : platformMetricsMetric === 'pickupGames' ? 'Pickup Games' : 'PRO Upgrades'}</span>
                  </span>
                  <span className="flex items-center space-x-1.5">
                    <span className="w-5 h-0 border-t-2 border-dashed border-amber-400 inline-block" />
                    <span className="text-amber-300 font-bold">7-Day Rolling Avg Trend Line</span>
                  </span>
                </div>
                <span>Updated in real-time via telemetry feed</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION 3: PARK VERIFICATION ================= */}
      {adminSection === 'parks' && (
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-lime-400" />
                <h3 className="font-black italic uppercase text-white text-lg">
                  Park Submissions & Validation Queue
                </h3>
              </div>
              <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
                Validate user-submitted parks before full verification on the Court Map.
              </p>
            </div>

            <div className="flex items-center bg-indigo-950 p-1 rounded-2xl border border-white/10">
              <button
                onClick={() => setParkFilter('pending')}
                className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1 ${
                  parkFilter === 'pending' ? 'bg-amber-400 text-black' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Pending ({pendingCourts.length})</span>
              </button>
              <button
                onClick={() => setParkFilter('verified')}
                className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1 ${
                  parkFilter === 'verified' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Verified ({verifiedCourts.length})</span>
              </button>
              <button
                onClick={() => setParkFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                  parkFilter === 'all' ? 'bg-indigo-700 text-white' : 'text-indigo-300 hover:text-white'
                }`}
              >
                All Parks
              </button>
            </div>
          </div>

          {displayCourts.length === 0 ? (
            <div className="p-8 text-center bg-indigo-950/60 rounded-2xl border border-white/5">
              <CheckCircle2 className="w-8 h-8 text-lime-400 mx-auto mb-2 opacity-60" />
              <p className="text-xs font-bold text-indigo-200">No parks found in this queue.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {displayCourts.map((court) => (
                <div
                  key={court.id}
                  className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 flex flex-col justify-between space-y-3"
                >
                  <div className="flex items-start space-x-3">
                    <img
                      src={court.imageUrl}
                      alt={court.name}
                      className="w-16 h-16 rounded-xl object-cover border border-white/10 shrink-0"
                      referrerPolicy="no-referrer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black italic uppercase px-2 py-0.5 rounded-md bg-lime-400 text-black">
                          {court.sport}
                        </span>
                        {court.status === 'pending' ? (
                          <span className="text-[10px] font-black uppercase text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/30">
                            Pending Validation ⏳
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase text-lime-300 bg-lime-400/10 px-2 py-0.5 rounded-full border border-lime-400/30">
                            Verified ✓
                          </span>
                        )}
                      </div>

                      <h4 className="font-extrabold text-sm text-white mt-1 line-clamp-1">{court.name}</h4>
                      <p className="text-[11px] text-indigo-200/70 line-clamp-1">{court.address}</p>
                      <div className="text-[10px] text-indigo-300/80 mt-1 flex items-center justify-between">
                        <span>Submitted by: <span className="font-bold text-white">{court.createdByName || 'Community Athlete'}</span></span>
                        <span className="font-mono text-indigo-300">Lat: {court.lat?.toFixed(3)}, Lng: {court.lng?.toFixed(3)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Geo API Audit & Duplicate Scan Widget */}
                  <div className="bg-indigo-900/60 p-2.5 rounded-xl border border-white/10 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase text-indigo-200 flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-lime-400" />
                        <span>Geo Coordinate & Duplicate Audit</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRunGeoAudit(court)}
                        className="px-2 py-0.5 bg-lime-400/20 hover:bg-lime-400/30 text-lime-300 border border-lime-400/40 rounded-lg text-[10px] font-bold flex items-center gap-1 transition"
                      >
                        <Search className="w-3 h-3" />
                        <span>Run Scan</span>
                      </button>
                    </div>

                    {geoAuditResults[court.id] ? (
                      <div className="space-y-1 pt-1 border-t border-white/5 text-[11px]">
                        <div className="flex items-center justify-between font-mono text-[10px]">
                          <span className={geoAuditResults[court.id].isValidGeo ? 'text-lime-400 font-bold' : 'text-rose-400 font-bold'}>
                            {geoAuditResults[court.id].isValidGeo ? '✓ Lat/Lng Coordinates Valid' : '✕ Invalid Coordinates'}
                          </span>
                          <span className="text-indigo-300/60">Scanned at {geoAuditResults[court.id].checkedAt}</span>
                        </div>
                        {geoAuditResults[court.id].duplicateWarning ? (
                          <div className="p-1.5 bg-rose-500/20 border border-rose-500/40 rounded-lg text-rose-200 font-semibold text-[10px]">
                            {geoAuditResults[court.id].duplicateWarning}
                          </div>
                        ) : (
                          <div className="p-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 font-semibold text-[10px] flex items-center justify-between">
                            <span>✓ No duplicate venue listings within 0.3 miles.</span>
                            {geoAuditResults[court.id].distanceToNearestMi && (
                              <span className="font-mono text-[9px] text-indigo-300">Nearest: {geoAuditResults[court.id].distanceToNearestMi?.toFixed(2)} mi</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-indigo-300/70 italic">Click 'Run Scan' to validate coordinates & detect nearby venue duplicates.</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-white/10 text-xs">
                    <span className="text-[10px] text-indigo-300/60 font-semibold">
                      Surface: {court.surfaceType} | Lights: {court.lighting ? 'Yes' : 'No'}
                    </span>

                    {court.status === 'pending' ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            onValidateCourt(court.id, 'approve');
                            addAuditLog(`Approved park: ${court.name}`, 'park');
                          }}
                          className="px-3 py-1 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-[11px] uppercase rounded-xl flex items-center space-x-1 transition shadow-md"
                        >
                          <Check className="w-3.5 h-3.5" />
                          <span>Validate</span>
                        </button>
                        <button
                          onClick={() => {
                            onValidateCourt(court.id, 'reject');
                            addAuditLog(`Rejected park submission: ${court.name}`, 'park');
                          }}
                          className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 font-black italic text-[11px] uppercase rounded-xl flex items-center space-x-1 transition"
                        >
                          <X className="w-3.5 h-3.5" />
                          <span>Reject</span>
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-lime-400 font-bold italic">
                        Official Venue Verified ✓
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= SECTION 4: ADMIN AUDIT LOG ================= */}
      {adminSection === 'logs' && (
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6 animate-fadeIn">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/10">
            <div className="flex items-center space-x-3.5">
              <div className="w-11 h-11 rounded-2xl bg-lime-400/20 border border-lime-400/40 text-lime-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="font-black italic uppercase text-white text-xl">
                    Admin Audit Log & System Accountability
                  </h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase font-mono">
                    SECURE COMPLIANCE
                  </span>
                </div>
                <p className="text-xs text-indigo-200/70 mt-0.5 font-medium">
                  Detailed record of all critical administrative operations, security threshold updates, provider toggles, and user resets with administrator account attribution.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(auditLogs, null, 2));
                const downloadAnchor = document.createElement('a');
                downloadAnchor.setAttribute("href", dataStr);
                downloadAnchor.setAttribute("download", `admin_audit_log_${Date.now()}.json`);
                document.body.appendChild(downloadAnchor);
                downloadAnchor.click();
                downloadAnchor.remove();
                triggerAuthToast('Admin Audit Trail Exported', 'JSON File Downloaded');
              }}
              className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-900 border border-white/15 text-lime-300 font-black italic uppercase text-xs rounded-2xl shadow-md transition flex items-center space-x-2 shrink-0"
            >
              <Download className="w-4 h-4" />
              <span>Export Audit Trail (JSON)</span>
            </button>
          </div>

          {/* Audit Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <div className="p-3 bg-indigo-950/80 border border-white/10 rounded-2xl">
              <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">Total Actions</div>
              <div className="text-xl font-black text-white">{auditLogs.length}</div>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl">
              <div className="text-[10px] font-mono text-amber-300 uppercase font-bold">Security Thresholds</div>
              <div className="text-xl font-black text-amber-300">
                {auditLogs.filter(l => l.category === 'SECURITY_THRESHOLD').length}
              </div>
            </div>
            <div className="p-3 bg-lime-400/10 border border-lime-400/30 rounded-2xl">
              <div className="text-[10px] font-mono text-lime-300 uppercase font-bold">Auth Config</div>
              <div className="text-xl font-black text-lime-400">
                {auditLogs.filter(l => l.category === 'AUTH_CONFIG').length}
              </div>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
              <div className="text-[10px] font-mono text-blue-300 uppercase font-bold">User Management</div>
              <div className="text-xl font-black text-blue-300">
                {auditLogs.filter(l => l.category === 'USER_MGMT').length}
              </div>
            </div>
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-2xl">
              <div className="text-[10px] font-mono text-rose-300 uppercase font-bold">System Resets</div>
              <div className="text-xl font-black text-rose-300">
                {auditLogs.filter(l => l.category === 'SYSTEM_RESET').length}
              </div>
            </div>
          </div>

          {/* Search Input & Category Filter Bar */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-2">
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap items-center gap-1.5 p-1 bg-indigo-950 rounded-xl border border-white/10 text-xs font-bold">
              {['ALL', 'SECURITY_THRESHOLD', 'AUTH_CONFIG', 'USER_MGMT', 'SYSTEM_RESET'].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setAuditLogCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-lg transition text-[11px] font-black uppercase italic ${
                    auditLogCategoryFilter === cat
                      ? 'bg-lime-400 text-black shadow-sm'
                      : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  {cat === 'ALL' ? `All (${auditLogs.length})` : cat.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* Text-Based Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-indigo-400 absolute left-3 top-3" />
              <input
                type="text"
                value={auditLogSearch}
                onChange={(e) => setAuditLogSearch(e.target.value)}
                placeholder="Search audit events by action, email, admin ID, or category..."
                className="w-full pl-9 pr-8 py-2 bg-indigo-950 border border-white/15 rounded-xl text-xs font-semibold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
              {auditLogSearch && (
                <button
                  onClick={() => setAuditLogSearch('')}
                  className="absolute right-3 top-2.5 text-indigo-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Audit Log Stream Cards */}
          <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
            {(() => {
              const filtered = auditLogs.filter(log => {
                if (auditLogCategoryFilter !== 'ALL' && log.category !== auditLogCategoryFilter) return false;

                if (auditLogSearch.trim()) {
                  const q = auditLogSearch.toLowerCase().trim();
                  const matchAction = log.action.toLowerCase().includes(q);
                  const matchAdmin = (log.adminEmail || '').toLowerCase().includes(q);
                  const matchType = log.type.toLowerCase().includes(q);
                  const matchCategory = (log.category || '').toLowerCase().includes(q);
                  const matchTime = log.time.toLowerCase().includes(q);
                  return matchAction || matchAdmin || matchType || matchCategory || matchTime;
                }

                return true;
              });

              if (filtered.length === 0) {
                return (
                  <div className="p-10 text-center text-xs text-indigo-300/60 font-semibold space-y-2 bg-indigo-950/60 rounded-2xl border border-white/10">
                    <ShieldAlert className="w-8 h-8 text-indigo-400/40 mx-auto" />
                    <p>No administrative audit log entries match your search criteria.</p>
                  </div>
                );
              }

              return filtered.map((log) => {
                let badgeColor = "bg-indigo-900 text-indigo-200 border-indigo-700";
                if (log.type === 'ban') badgeColor = "bg-rose-500/20 text-rose-300 border-rose-500/40";
                else if (log.type === 'suspend') badgeColor = "bg-amber-500/20 text-amber-300 border-amber-500/40";
                else if (log.type === 'reset') badgeColor = "bg-purple-500/20 text-purple-300 border-purple-500/40";
                else if (log.type === 'delete') badgeColor = "bg-rose-600/20 text-rose-200 border-rose-600/40";
                else if (log.type === 'update') badgeColor = "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
                else if (log.type === 'park') badgeColor = "bg-blue-500/20 text-blue-300 border-blue-500/40";

                return (
                  <div
                    key={log.id}
                    className="p-4 bg-indigo-950/90 hover:bg-indigo-950 border border-white/10 rounded-2xl transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-md"
                  >
                    <div className="flex items-start space-x-3.5">
                      <div className="w-9 h-9 rounded-xl bg-indigo-900 border border-white/10 flex items-center justify-center shrink-0 text-lime-400 font-mono text-xs font-bold mt-0.5">
                        <Terminal className="w-4 h-4" />
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-black text-white text-sm leading-tight">{log.action}</span>
                          {log.category && (
                            <span className="px-2 py-0.5 rounded-full bg-indigo-900/80 border border-indigo-400/30 text-[9px] font-mono text-lime-300 font-bold uppercase">
                              {log.category.replace('_', ' ')}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-indigo-300/80 font-mono">
                          <span className="flex items-center space-x-1 text-indigo-200 font-semibold">
                            <User className="w-3 h-3 text-lime-400 shrink-0" />
                            <span>{log.adminEmail || 'elmejor15@gmail.com (Superadmin)'}</span>
                          </span>
                          <span>•</span>
                          <span className="flex items-center space-x-1 text-indigo-300/70">
                            <Clock className="w-3 h-3 shrink-0" />
                            <span>{log.time}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0 self-start sm:self-center">
                      <span className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase font-mono italic tracking-wider ${badgeColor}`}>
                        {log.type}
                      </span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* ================= SECTION 5: OFFLINE SYNC ISSUES & QUEUE SUMMARY ================= */}
      {adminSection === 'sync' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Header Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-rose-500/30">
              <span className="text-[10px] font-black italic uppercase text-rose-300 block">
                Failed Syncs (Last 24h)
              </span>
              <div className="text-2xl font-black italic text-rose-400 mt-1 flex items-center justify-between">
                <span>{failedSyncList.filter(i => i.status === 'failed').length}</span>
                <WifiOff className="w-5 h-5 text-rose-400/80" />
              </div>
              <span className="text-[10px] text-rose-200/70 font-semibold mt-1 block">Intermittent Connections</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-amber-400/30">
              <span className="text-[10px] font-black italic uppercase text-amber-300 block">
                Avg Retry Attempts
              </span>
              <div className="text-2xl font-black italic text-amber-300 mt-1 flex items-center justify-between">
                <span>
                  {(failedSyncList.reduce((acc, curr) => acc + (curr.retryCount || 1), 0) / (failedSyncList.length || 1)).toFixed(1)}x
                </span>
                <RefreshCw className="w-5 h-5 text-amber-300/80" />
              </div>
              <span className="text-[10px] text-amber-200/70 font-semibold mt-1 block">Automatic Re-try Queue</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-lime-400/30">
              <span className="text-[10px] font-black italic uppercase text-lime-300 block">
                Sync Success Rate
              </span>
              <div className="text-2xl font-black italic text-lime-400 mt-1 flex items-center justify-between">
                <span>94.8%</span>
                <CheckCircle2 className="w-5 h-5 text-lime-400/80" />
              </div>
              <span className="text-[10px] text-lime-200/70 font-semibold mt-1 block">High Availability Service</span>
            </div>

            <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <span className="text-[10px] font-black italic uppercase text-indigo-300 block">
                Queue Buffer Engine
              </span>
              <div className="text-2xl font-black italic text-white mt-1 flex items-center justify-between">
                <span>IndexedDB</span>
                <Database className="w-5 h-5 text-indigo-300/80" />
              </div>
              <span className="text-[10px] text-indigo-200/70 font-semibold mt-1 block">Local Client Storage</span>
            </div>
          </div>

          {/* Sync Filter & Bulk Controls */}
          <div className="bg-indigo-900/60 p-5 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h3 className="font-black italic uppercase text-white text-lg flex items-center space-x-2">
                  <WifiOff className="w-5 h-5 text-amber-400" />
                  <span>Offline Sync Failures Diagnostic Log</span>
                </h3>
                <p className="text-xs text-indigo-200/70 font-semibold">
                  Summarized offline queue operations failed due to intermittent client cellular or Wi-Fi drops.
                </p>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setFailedSyncList(prev => prev.map(item => ({ ...item, status: 'synced' as const })));
                    setActionNotice('⚡ Executed bulk sync re-try! All 24h failed OfflineQueueItems processed successfully.');
                  }}
                  className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl transition shadow-md flex items-center space-x-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Bulk Retry All Syncs</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const reportJson = JSON.stringify(failedSyncList, null, 2);
                    const blob = new Blob([reportJson], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `offline_sync_failures_${Date.now()}.json`;
                    a.click();
                    setActionNotice('📄 Diagnostic report generated & downloaded as JSON.');
                  }}
                  className="px-3.5 py-2 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 font-bold uppercase text-xs rounded-xl border border-white/10 transition flex items-center space-x-1.5"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Export Log</span>
                </button>
              </div>
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/10">
              <span className="text-[10px] font-black uppercase text-indigo-300 mr-2">Filter Action:</span>
              {['all', 'LOG_GAME', 'POST_CHAT', 'HOST_GAME', 'UPDATE_PROFILE'].map((act) => (
                <button
                  key={act}
                  type="button"
                  onClick={() => setSyncFilterType(act)}
                  className={`px-3 py-1 rounded-lg text-xs font-black uppercase transition ${
                    syncFilterType === act ? 'bg-amber-400 text-black' : 'bg-indigo-950/80 text-indigo-300 hover:text-white border border-white/5'
                  }`}
                >
                  {act === 'all' ? 'All Types' : act}
                </button>
              ))}

              <div className="ml-auto flex items-center space-x-1">
                <span className="text-[10px] font-black uppercase text-indigo-300 mr-1">Status:</span>
                {['all', 'failed', 'synced'].map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setSyncStatusFilter(st as any)}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase transition ${
                      syncStatusFilter === st ? 'bg-indigo-700 text-white' : 'text-indigo-400 hover:text-white'
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>

            {/* Items Table / Cards */}
            <div className="space-y-3 pt-2">
              {failedSyncList
                .filter(item => syncFilterType === 'all' || item.type === syncFilterType)
                .filter(item => syncStatusFilter === 'all' || item.status === syncStatusFilter)
                .map((item) => {
                  const payloadName = item.payload?.athleteName || item.payload?.athleteId || 'Athlete';
                  const timeAgo = Math.round((Date.now() - new Date(item.createdAt).getTime()) / (1000 * 3600));
                  return (
                    <div
                      key={item.id}
                      className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 hover:border-amber-400/40 transition flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                    >
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-[10px] font-black font-mono uppercase rounded border border-amber-400/30">
                            {item.type}
                          </span>
                          <span className="text-[10px] font-mono text-indigo-300/60">
                            {timeAgo === 0 ? 'Just now' : `${timeAgo}h ago`} ({item.id})
                          </span>
                          {item.status === 'synced' ? (
                            <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[9px] font-bold uppercase rounded">
                              Synced ✓
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 text-[9px] font-bold uppercase rounded">
                              Failed ({item.retryCount || 1} retries)
                            </span>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-extrabold text-white">{payloadName}</span>
                          <span className="text-indigo-300/60 text-xs">•</span>
                          <span className="text-xs text-indigo-200/80 truncate">
                            {item.payload?.sport ? `Sport: ${item.payload.sport.toUpperCase()}` : ''}{' '}
                            {item.payload?.title || item.payload?.messageText || item.payload?.bio || ''}
                          </span>
                        </div>

                        <div className="p-2 bg-rose-950/40 border border-rose-500/30 rounded-xl text-[11px] text-rose-300 font-mono">
                          ⚠️ {item.errorMessage || 'Network submission error during client offline buffer flush.'}
                        </div>
                      </div>

                      {/* Item Actions */}
                      <div className="flex items-center space-x-2 self-end sm:self-auto shrink-0">
                        {item.status !== 'synced' && (
                          <button
                            type="button"
                            onClick={() => {
                              setFailedSyncList(prev =>
                                prev.map(i => (i.id === item.id ? { ...i, status: 'synced' } : i))
                              );
                              setActionNotice(`⚡ Synced OfflineQueueItem #${item.id} successfully to remote DB!`);
                            }}
                            className="px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-[10px] rounded-xl transition shadow flex items-center space-x-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            <span>Retry Sync</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setFailedSyncList(prev => prev.filter(i => i.id !== item.id));
                            setActionNotice(`🧹 Cleared item #${item.id} from offline issue queue.`);
                          }}
                          className="px-2.5 py-1.5 bg-indigo-900 hover:bg-rose-900 text-indigo-300 hover:text-white text-[10px] font-bold uppercase rounded-xl transition"
                          title="Dismiss item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION 7: SPONSORS & ADVERTISING CONTROL ================= */}
      {adminSection === 'sponsors' && (
        <SponsorManagementTab
          sponsors={sponsors}
          onAddSponsor={onAddSponsor}
          onUpdateSponsor={onUpdateSponsor}
          onDeleteSponsor={onDeleteSponsor}
          onResetSponsors={onResetSponsors}
        />
      )}

      {/* ================= MODAL: TEMP PASSWORD GENERATED ================= */}
      {tempPasswordModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-md bg-indigo-950 border border-white/20 rounded-[2.5rem] p-6 space-y-5 text-center shadow-2xl">
            <div className="w-12 h-12 bg-lime-400/20 text-lime-400 rounded-2xl flex items-center justify-center mx-auto border border-lime-400/30">
              <Key className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <h3 className="text-xl font-black italic uppercase text-white">Temporary Passcode Issued</h3>
              <p className="text-xs text-indigo-200/70 font-semibold">
                Issued for <span className="text-white font-bold">{tempPasswordModal.userName}</span>.
              </p>
            </div>

            <div className="p-4 bg-indigo-900/90 border border-lime-400/30 rounded-2xl space-y-1">
              <span className="text-[10px] font-black uppercase text-indigo-300/60 block">New Temporary Passcode</span>
              <div className="text-xl font-mono font-black text-lime-400 tracking-wider">
                {tempPasswordModal.tempPass}
              </div>
            </div>

            <p className="text-[11px] text-indigo-300/70 italic">
              Share this passcode securely with the user. They will be prompted to set a permanent password upon sign in.
            </p>

            <button
              onClick={() => setTempPasswordModal(null)}
              className="w-full py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-2xl transition text-xs"
            >
              Done & Close
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL: SUSPEND USER ================= */}
      {suspendModalUser && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-md bg-indigo-950 border border-white/20 rounded-[2.5rem] p-6 space-y-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-lg font-black italic uppercase text-white">Suspend Athlete Account</h3>
              </div>
              <button onClick={() => setSuspendModalUser(null)} className="text-indigo-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-indigo-200/80 font-semibold">
              Suspending <span className="text-white font-black">{suspendModalUser.name}</span> ({suspendModalUser.handle}). This temporarily revokes their ability to post games or chats.
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">Suspension Reason</label>
                <input
                  type="text"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  className="w-full bg-indigo-900 border border-white/10 rounded-xl px-3 py-2 font-semibold text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-2">
              <button
                onClick={() => setSuspendModalUser(null)}
                className="flex-1 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black italic uppercase rounded-2xl text-xs transition"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  onUpdateUserStatus(suspendModalUser.id, 'suspended', suspendReason);
                  setActionNotice(`Suspended user ${suspendModalUser.name}`);
                  addAuditLog(`Suspended user ${suspendModalUser.name}: ${suspendReason}`, 'suspend');
                  setSuspendModalUser(null);
                }}
                className="flex-1 py-2.5 bg-amber-400 hover:bg-amber-300 text-black font-black italic uppercase rounded-2xl text-xs transition"
              >
                Confirm Suspension
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: CONFIRM GRANT PRO ACCESS ================= */}
      {grantProModalUser && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-md bg-indigo-950 border-2 border-amber-400/80 rounded-[2.5rem] p-6 sm:p-8 space-y-6 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3 text-amber-300">
                <div className="p-2.5 bg-amber-400/20 rounded-2xl border border-amber-400/40">
                  <Crown className="w-6 h-6 text-amber-400 fill-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black italic uppercase text-white">Confirm Grant PRO Access</h3>
                  <span className="text-[10px] font-mono text-amber-300 uppercase tracking-widest font-bold">SUBSCRIPTION UPGRADE</span>
                </div>
              </div>
              <button onClick={() => setGrantProModalUser(null)} className="text-indigo-300 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-indigo-900/60 rounded-2xl border border-white/10">
                <img
                  src={grantProModalUser.avatar}
                  alt={grantProModalUser.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400/50"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <div className="font-extrabold text-white text-sm">{grantProModalUser.name}</div>
                  <div className="text-xs text-indigo-300/80 font-mono">{grantProModalUser.handle}</div>
                  <div className="text-[10px] text-indigo-300 font-bold mt-0.5 flex items-center gap-1">
                    <Users className="w-3 h-3 text-indigo-400" />
                    <span>Currently FREE Tier Member</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-400/10 border border-amber-400/30 rounded-2xl text-amber-200 text-xs leading-relaxed space-y-2">
                <p className="font-bold flex items-center space-x-1.5 text-amber-300">
                  <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Upgrade to Playground PRO Tier</span>
                </p>
                <p className="text-[11px] text-amber-100/80 font-normal">
                  Are you sure you want to grant PRO status to <strong>{grantProModalUser.name}</strong>? This will immediately unlock deep historical D3 analytics, AI scouting summaries, and wearable device sync across their account.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setGrantProModalUser(null)}
                className="flex-1 py-3 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black italic uppercase rounded-2xl text-xs transition"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  if (onUpgradeUserPro) {
                    onUpgradeUserPro(grantProModalUser.id, true);
                  }
                  setActionNotice(`Upgraded ${grantProModalUser.name} to PRO Tier 👑`);
                  addAuditLog(`Admin Upgraded ${grantProModalUser.name} (${grantProModalUser.handle}) to PRO Tier`, 'reset');
                  setGrantProModalUser(null);
                }}
                className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase rounded-2xl text-xs transition shadow-lg shadow-amber-400/30 flex items-center justify-center space-x-1.5"
              >
                <Crown className="w-4 h-4 fill-black" />
                <span>Confirm Grant PRO</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: CONFIRM REVOKE PRO ACCESS ================= */}
      {revokeProModalUser && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-md bg-indigo-950 border-2 border-rose-500/80 rounded-[2.5rem] p-6 sm:p-8 space-y-6 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3 text-rose-400">
                <div className="p-2.5 bg-rose-500/20 rounded-2xl border border-rose-500/40">
                  <Crown className="w-6 h-6 text-rose-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black italic uppercase text-white">Confirm Revoke PRO Access</h3>
                  <span className="text-[10px] font-mono text-rose-400 uppercase tracking-widest font-bold">SUBSCRIPTION DEMOTION</span>
                </div>
              </div>
              <button onClick={() => setRevokeProModalUser(null)} className="text-indigo-300 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-indigo-900/60 rounded-2xl border border-white/10">
                <img
                  src={revokeProModalUser.avatar}
                  alt={revokeProModalUser.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-rose-500/50"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <div className="font-extrabold text-white text-sm">{revokeProModalUser.name}</div>
                  <div className="text-xs text-indigo-300/80 font-mono">{revokeProModalUser.handle}</div>
                  <div className="text-[10px] text-amber-300 font-bold mt-0.5 flex items-center gap-1">
                    <Crown className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span>Current PRO Tier Member</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-300 text-xs leading-relaxed space-y-2">
                <p className="font-bold flex items-center space-x-1.5">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>Warning: Downgrading to FREE Tier</span>
                </p>
                <p className="text-[11px] text-rose-200/80 font-normal">
                  Are you sure you want to revoke PRO access for <strong>{revokeProModalUser.name}</strong>? This will set their subscription tier to FREE and restrict access to advanced D3 analytics, scouting summaries, and wearable device synchronization.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setRevokeProModalUser(null)}
                className="flex-1 py-3 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black italic uppercase rounded-2xl text-xs transition"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  if (onUpgradeUserPro) {
                    onUpgradeUserPro(revokeProModalUser.id, false);
                  }
                  setActionNotice(`Revoked PRO Access from ${revokeProModalUser.name} ❌`);
                  addAuditLog(`Admin Revoked PRO Tier access from ${revokeProModalUser.name} (${revokeProModalUser.handle})`, 'reset');
                  setRevokeProModalUser(null);
                }}
                className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 text-white font-black italic uppercase rounded-2xl text-xs transition shadow-lg shadow-rose-600/30 flex items-center justify-center space-x-1.5"
              >
                <Crown className="w-4 h-4" />
                <span>Confirm Revoke PRO</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION 8: AUTH & SOCIAL LOGINS MANAGEMENT ================= */}
      {adminSection === 'auth_settings' && (
        <div className="space-y-6">
          <div className="bg-indigo-900/60 p-6 rounded-[2rem] border border-white/10 shadow-xl space-y-6">
            {/* Quick Bulk Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/10">
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-black italic uppercase text-white flex items-center space-x-2">
                    <Lock className="w-5 h-5 text-amber-400" />
                    <span>Authentication Management & Social Logins</span>
                  </h2>
                  <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[10px] font-black uppercase flex items-center space-x-1">
                    <CheckCircle className="w-3 h-3 text-lime-400" />
                    <span>FIRESTORE DB SYNCED</span>
                  </span>
                </div>
                <p className="text-xs text-indigo-200/70 mt-1 font-medium">
                  Toggle availability for Google, Facebook, and Apple authentication providers in real-time. Changes are instantly saved to Firestore and synchronized across all live clients.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  id="btn-open-reset-entire-settings-modal"
                  onClick={() => setIsResetSettingsConfirmModalOpen(true)}
                  className="px-3.5 py-2 bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white font-black italic uppercase text-[11px] rounded-xl shadow-lg transition flex items-center space-x-1.5 border border-white/20 cursor-pointer"
                  title="Reverts all toggles, whitelists, rate limits, and thresholds to original default values in Firestore"
                >
                  <RotateCcw className="w-4 h-4 stroke-[2.5]" />
                  <span>Reset Entire Application Settings</span>
                </button>

                <button
                  type="button"
                  onClick={handleResetUIPreferences}
                  className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-[11px] rounded-xl shadow-lg transition flex items-center space-x-1.5"
                  title="Programmatically reset all UI toggles to default configuration and sync immediately to Firestore"
                >
                  <RotateCcw className="w-4 h-4 stroke-[2.5]" />
                  <span>Reset UI Preferences</span>
                </button>

                <button
                  type="button"
                  onClick={handleResetToDefaultAuthSettings}
                  className="px-3.5 py-2 bg-indigo-950 hover:bg-indigo-900 text-lime-300 border border-lime-400/40 rounded-xl font-black italic uppercase text-[11px] transition flex items-center space-x-1.5 shadow-md"
                >
                  <RotateCcw className="w-4 h-4 text-lime-400" />
                  <span>Reset to Default Auth</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const settings = {
                      googleEnabled: false,
                      facebookEnabled: false,
                      appleEnabled: false,
                    };
                    setLocalAuthSettings(settings);
                    onUpdateAuthProviderSettings?.(settings);
                    triggerAuthToast('All Social Logins', false);
                    setActionNotice('Disabled all social OAuth logins 🚫 — Settings saved to Firestore Database');
                    addAuditLog('Admin disabled Apple, Google, and Facebook OAuth sign-ins', 'update');
                  }}
                  className="px-3.5 py-2 bg-rose-500/20 hover:bg-rose-500 text-rose-300 hover:text-white border border-rose-500/40 rounded-xl font-black italic uppercase text-[11px] transition flex items-center space-x-1.5"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Disable All Social Logins</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const settings = {
                      googleEnabled: true,
                      facebookEnabled: true,
                      appleEnabled: true,
                    };
                    setLocalAuthSettings(settings);
                    onUpdateAuthProviderSettings?.(settings);
                    triggerAuthToast('All Social Logins', true);
                    setActionNotice('Enabled all social OAuth logins ✅ — Settings saved to Firestore Database');
                    addAuditLog('Admin enabled Apple, Google, and Facebook OAuth sign-ins', 'update');
                  }}
                  className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-[11px] rounded-xl shadow-md transition flex items-center space-x-1.5"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Enable All Social Logins</span>
                </button>
              </div>
            </div>

            {/* Provider Toggle Grid Sub-Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black italic uppercase tracking-wider text-indigo-200 flex items-center space-x-2">
                  <ShieldCheck className="w-4 h-4 text-lime-400" />
                  <span>OAuth & SSO Provider Grid & Display Order</span>
                </h3>
                <span className="text-[10px] text-indigo-300 font-mono">Collection: settings/authProviderSettings</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Dynamically render cards according to current providerOrder or default */}
                {(localAuthSettings.providerOrder && localAuthSettings.providerOrder.length > 0
                  ? localAuthSettings.providerOrder
                  : ['google', 'facebook', 'apple']
                ).map((key, posIdx) => {
                  if (key === 'google') {
                    return (
                      <div key="google" className={`p-5 rounded-2xl border transition-all space-y-4 ${
                        localAuthSettings.googleEnabled
                          ? 'bg-indigo-950/90 border-lime-400/50 shadow-lg shadow-lime-400/10'
                          : 'bg-indigo-950/40 border-white/10 opacity-90'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-900 border border-white/20 flex items-center justify-center shrink-0">
                              <Chrome className="w-5 h-5 text-rose-400" />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-extrabold text-sm text-white">Google</h4>
                                <span className="px-2 py-0.5 rounded-full bg-indigo-900 border border-indigo-400/40 text-[9px] font-black font-mono text-lime-300">
                                  ORDER #{posIdx + 1}
                                </span>
                                <div className="relative group inline-block">
                                  <button type="button" className="p-1 text-indigo-300 hover:text-lime-300 transition focus:outline-none" aria-label="Google OAuth Info">
                                    <Info className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:flex group-focus-within:flex flex-col w-60 p-3 bg-neutral-950 text-white text-[11px] font-medium rounded-xl border border-white/20 shadow-2xl z-40 pointer-events-none">
                                    <span className="font-bold text-lime-400 mb-1 flex items-center gap-1">
                                      <Info className="w-3 h-3 shrink-0" /> Public Auth View Impact:
                                    </span>
                                    <span className="text-indigo-100/90 leading-normal">
                                      Disabling Google OAuth hides the "Sign in with Google OAuth" button on PublicAuthView. Users will be directed to use Email/Password or active providers.
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[10px] text-indigo-300/80 font-mono">Google OAuth 2.0</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {localAuthSettings.googleEnabled ? (
                              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                <span>Enabled</span>
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 rounded-full bg-slate-700/60 text-slate-400 border border-slate-600/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                                <span>Disabled</span>
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => handleToggleProvider('googleEnabled', 'Google')}
                              className={`w-12 h-6 rounded-full transition-colors p-1 flex items-center ${
                                localAuthSettings.googleEnabled ? 'bg-lime-400 justify-end' : 'bg-slate-700 justify-start'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-full shadow-md ${
                                localAuthSettings.googleEnabled ? 'bg-black' : 'bg-white'
                              }`} />
                            </button>
                          </div>
                        </div>

                        <p className="text-xs text-indigo-200/70 font-medium leading-relaxed">
                          Controls Google OAuth sign-in availability on the public authentication page and modals.
                        </p>

                        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
                          <span className="text-indigo-300/70 font-semibold">Display Order:</span>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              disabled={posIdx === 0}
                              onClick={() => handleMoveProviderOrder('google', 'up')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              ← Move First / Left
                            </button>
                            <button
                              type="button"
                              disabled={posIdx === 2}
                              onClick={() => handleMoveProviderOrder('google', 'down')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              Move Right →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (key === 'facebook') {
                    return (
                      <div key="facebook" className={`p-5 rounded-2xl border transition-all space-y-4 ${
                        localAuthSettings.facebookEnabled
                          ? 'bg-indigo-950/90 border-lime-400/50 shadow-lg shadow-lime-400/10'
                          : 'bg-indigo-950/40 border-white/10 opacity-90'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-[#1877F2] border border-white/20 flex items-center justify-center shrink-0">
                              <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-extrabold text-sm text-white">Facebook</h4>
                                <span className="px-2 py-0.5 rounded-full bg-indigo-900 border border-indigo-400/40 text-[9px] font-black font-mono text-lime-300">
                                  ORDER #{posIdx + 1}
                                </span>
                                <div className="relative group inline-block">
                                  <button type="button" className="p-1 text-indigo-300 hover:text-lime-300 transition focus:outline-none" aria-label="Facebook Info">
                                    <Info className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:flex group-focus-within:flex flex-col w-60 p-3 bg-neutral-950 text-white text-[11px] font-medium rounded-xl border border-white/20 shadow-2xl z-40 pointer-events-none">
                                    <span className="font-bold text-lime-400 mb-1 flex items-center gap-1">
                                      <Info className="w-3 h-3 shrink-0" /> Public Auth View Impact:
                                    </span>
                                    <span className="text-indigo-100/90 leading-normal">
                                      Disabling Facebook removes the Meta SSO option on PublicAuthView. Users will fall back to Email/Password or active providers.
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[10px] text-indigo-300/80 font-mono">Meta SSO Login</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {localAuthSettings.facebookEnabled ? (
                              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                <span>Enabled</span>
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 rounded-full bg-slate-700/60 text-slate-400 border border-slate-600/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                                <span>Disabled</span>
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => handleToggleProvider('facebookEnabled', 'Facebook')}
                              className={`w-12 h-6 rounded-full transition-colors p-1 flex items-center ${
                                localAuthSettings.facebookEnabled ? 'bg-lime-400 justify-end' : 'bg-slate-700 justify-start'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-full shadow-md ${
                                localAuthSettings.facebookEnabled ? 'bg-black' : 'bg-white'
                              }`} />
                            </button>
                          </div>
                        </div>

                        <p className="text-xs text-indigo-200/70 font-medium leading-relaxed">
                          Controls Facebook OAuth sign-in availability on the public authentication page and modals.
                        </p>

                        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
                          <span className="text-indigo-300/70 font-semibold">Display Order:</span>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              disabled={posIdx === 0}
                              onClick={() => handleMoveProviderOrder('facebook', 'up')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              ← Move Left
                            </button>
                            <button
                              type="button"
                              disabled={posIdx === 2}
                              onClick={() => handleMoveProviderOrder('facebook', 'down')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              Move Right →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (key === 'apple') {
                    return (
                      <div key="apple" className={`p-5 rounded-2xl border transition-all space-y-4 ${
                        localAuthSettings.appleEnabled
                          ? 'bg-indigo-950/90 border-lime-400/50 shadow-lg shadow-lime-400/10'
                          : 'bg-indigo-950/40 border-white/10 opacity-90'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-black border border-white/20 flex items-center justify-center shrink-0">
                              <svg className="w-5 h-5 fill-white" viewBox="0 0 170 170">
                                <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.34.13-9.14-1.9-14.4-6.08-3.38-2.73-7.25-7.39-11.63-13.98-7.39-11.05-12.82-22.92-16.29-35.6-3.48-12.68-5.21-24.89-5.21-36.63 0-15.65 3.86-28.52 11.57-38.6 7.71-10.08 17.27-15.22 28.67-15.43 4.23 0 9.17 1.13 14.82 3.39 5.65 2.26 9.68 3.39 12.09 3.39 2.12 0 6.27-1.18 12.44-3.54 6.18-2.36 11.13-3.44 14.86-3.24 10.23.53 18.66 4.12 25.29 10.77 6.63 6.65 10.73 14.88 12.3 24.69-9.14 5.54-13.63 13.38-13.48 23.52.16 7.97 3.12 14.81 8.89 20.52 5.77 5.71 12.79 8.92 21.06 9.63-1.89 5.76-4.22 11.41-6.99 16.95zM119.22 31.8c0-6.73 2.45-13.11 7.36-19.14 4.91-6.03 11.08-9.84 18.52-11.43.32 1.48.48 2.8.48 3.96 0 6.73-2.52 13.16-7.56 19.29-5.04 6.13-11.18 9.94-18.42 11.43-.11-.96-.38-2.33-.38-4.11z" />
                              </svg>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-extrabold text-sm text-white">Apple</h4>
                                <span className="px-2 py-0.5 rounded-full bg-indigo-900 border border-indigo-400/40 text-[9px] font-black font-mono text-lime-300">
                                  ORDER #{posIdx + 1}
                                </span>
                                <div className="relative group inline-block">
                                  <button type="button" className="p-1 text-indigo-300 hover:text-lime-300 transition focus:outline-none" aria-label="Apple ID Info">
                                    <Info className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:flex group-focus-within:flex flex-col w-60 p-3 bg-neutral-950 text-white text-[11px] font-medium rounded-xl border border-white/20 shadow-2xl z-40 pointer-events-none">
                                    <span className="font-bold text-lime-400 mb-1 flex items-center gap-1">
                                      <Info className="w-3 h-3 shrink-0" /> Public Auth View Impact:
                                    </span>
                                    <span className="text-indigo-100/90 leading-normal">
                                      Disabling Apple ID removes Apple Sign-In on PublicAuthView. Users will be prompted to use Email/Password or active alternatives.
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[10px] text-indigo-300/80 font-mono">Sign in with Apple</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {localAuthSettings.appleEnabled ? (
                              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                <span>Enabled</span>
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 rounded-full bg-slate-700/60 text-slate-400 border border-slate-600/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                                <span>Disabled</span>
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => handleToggleProvider('appleEnabled', 'Apple')}
                              className={`w-12 h-6 rounded-full transition-colors p-1 flex items-center ${
                                localAuthSettings.appleEnabled ? 'bg-lime-400 justify-end' : 'bg-slate-700 justify-start'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-full shadow-md ${
                                localAuthSettings.appleEnabled ? 'bg-black' : 'bg-white'
                              }`} />
                            </button>
                          </div>
                        </div>

                        <p className="text-xs text-indigo-200/70 font-medium leading-relaxed">
                          Controls Sign in with Apple availability on the public authentication page and modals.
                        </p>

                        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
                          <span className="text-indigo-300/70 font-semibold">Display Order:</span>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              disabled={posIdx === 0}
                              onClick={() => handleMoveProviderOrder('apple', 'up')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              ← Move Left
                            </button>
                            <button
                              type="button"
                              disabled={posIdx === 2}
                              onClick={() => handleMoveProviderOrder('apple', 'down')}
                              className="px-2 py-1 bg-indigo-900 hover:bg-indigo-800 disabled:opacity-30 disabled:cursor-not-allowed text-lime-400 font-bold rounded-lg border border-white/10 text-[10px]"
                            >
                              Move Right →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            </div>

            {/* 7-Day Login Attempt Frequency Line Chart Sub-Section */}
            <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-4 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div className="flex items-center space-x-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-indigo-500/20 border border-indigo-400/40 text-lime-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                    <Activity className="w-6 h-6 text-lime-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-extrabold text-base text-white">7-Day Authentication Activity Line Chart</h4>
                      <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase font-mono">
                        RECHARTS VISUALIZATION
                      </span>
                    </div>
                    <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                      Frequency of login attempts over the last 7 days, highlighting failed vs. successful attempts for quick security assessment.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-xs font-mono">
                  <div className="flex items-center space-x-1.5 text-lime-400 font-bold">
                    <span className="w-3 h-3 rounded-full bg-lime-400 inline-block" />
                    <span>Successful Logins</span>
                  </div>
                  <div className="flex items-center space-x-1.5 text-rose-400 font-bold">
                    <span className="w-3 h-3 rounded-full bg-rose-500 inline-block" />
                    <span>Failed / Blocked</span>
                  </div>
                </div>
              </div>

              <div className="h-64 sm:h-72 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={loginAttempts7DaysData} margin={{ top: 10, right: 15, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.35} />
                    <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#090d16',
                        borderColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '1rem',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '11px' }} />
                    <Line
                      type="monotone"
                      dataKey="successful"
                      name="Successful Logins"
                      stroke="#a3e635"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#a3e635', strokeWidth: 2, stroke: '#000' }}
                      activeDot={{ r: 7 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="failed"
                      name="Failed / Blocked Attempts"
                      stroke="#f43f5e"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#f43f5e', strokeWidth: 2, stroke: '#000' }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Security Thresholds & Auto Account Suspension Sub-Section */}
            <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-5 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div className="flex items-center space-x-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-amber-400/20 border border-amber-400/40 text-amber-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                    <ShieldAlert className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-extrabold text-base text-white">Security Thresholds & Auto Account Suspension</h4>
                      <span className="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[10px] font-black uppercase font-mono">
                        BRUTE-FORCE MITIGATION
                      </span>
                    </div>
                    <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                      Define maximum failed login attempts per account before triggering automatic temporary suspension, with an administrative override toggle.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      const nextVal = !(localAuthSettings.autoSuspensionEnabled !== false);
                      const updated: AuthProviderSettings = {
                        ...localAuthSettings,
                        autoSuspensionEnabled: nextVal,
                      };
                      setLocalAuthSettings(updated);
                      triggerAuthToast('Auto-Suspension Override', nextVal);
                      setActionNotice(`Auto Account Suspension ${nextVal ? 'ENABLED' : 'DISABLED'} (Threshold: ${localAuthSettings.maxFailedAttemptsLimit || 5} Failed Logins)`);
                      onUpdateAuthProviderSettings?.(updated);
                      addAuditLog(`Admin ${nextVal ? 'enabled' : 'disabled'} auto account suspension threshold override`, 'update', 'SECURITY_THRESHOLD');
                    }}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 border shadow-md ${
                      localAuthSettings.autoSuspensionEnabled !== false
                        ? 'bg-amber-400 text-black border-amber-300'
                        : 'bg-slate-800 text-slate-300 border-white/10'
                    }`}
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span>Override Switch: {localAuthSettings.autoSuspensionEnabled !== false ? 'ACTIVE (AUTO SUSPEND ON)' : 'OFF (DISABLED)'}</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Failed Attempt Limit Control */}
                <div className="p-4 bg-indigo-900/50 rounded-2xl border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black uppercase text-indigo-200 flex items-center space-x-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span>Maximum Failed Login Limit</span>
                    </label>
                    <span className="text-[11px] font-mono text-lime-400 font-bold">
                      CURRENT: {localAuthSettings.maxFailedAttemptsLimit || 5} ATTEMPTS
                    </span>
                  </div>

                  <p className="text-[11px] text-indigo-200/70">
                    If an athlete or scout enters invalid passwords consecutively matching this limit, their account status is automatically flipped to <strong className="text-amber-300">SUSPENDED</strong>.
                  </p>

                  {/* Preset Limit Buttons */}
                  <div className="flex items-center space-x-2 pt-1">
                    <span className="text-[10px] font-mono text-indigo-300 uppercase">Presets:</span>
                    {[3, 5, 8, 10].map((presetLimit) => {
                      const isSelected = (localAuthSettings.maxFailedAttemptsLimit || 5) === presetLimit;
                      return (
                        <button
                          key={presetLimit}
                          type="button"
                          onClick={() => {
                            const updated: AuthProviderSettings = {
                              ...localAuthSettings,
                              maxFailedAttemptsLimit: presetLimit,
                            };
                            setLocalAuthSettings(updated);
                            triggerAuthToast('Security Limit Set', `${presetLimit} Attempts`);
                            setActionNotice(`Set maximum failed login attempt limit to ${presetLimit} attempts per account.`);
                            onUpdateAuthProviderSettings?.(updated);
                            addAuditLog(`Admin set maximum failed login attempt threshold to ${presetLimit} attempts`, 'update', 'SECURITY_THRESHOLD');
                          }}
                          className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition border ${
                            isSelected
                              ? 'bg-amber-400 text-black border-amber-300 font-black shadow-md'
                              : 'bg-indigo-950 text-indigo-200 hover:text-white border-white/10'
                          }`}
                        >
                          {presetLimit} Limits
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active Protection Rule Summary Card */}
                <div className="p-4 bg-indigo-900/50 rounded-2xl border border-white/10 space-y-3 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-black uppercase text-indigo-200 flex items-center space-x-1.5">
                      <Info className="w-4 h-4 text-lime-400" />
                      <span>Active Protection Rule Summary</span>
                    </div>
                    <p className="text-xs text-indigo-200/80 mt-1.5 leading-relaxed">
                      {localAuthSettings.autoSuspensionEnabled !== false ? (
                        <span className="text-lime-300">
                          ⚡ <strong>ACTIVE THRESHOLD:</strong> Any account exceeding <strong>{localAuthSettings.maxFailedAttemptsLimit || 5} consecutive invalid password submissions</strong> will be locked & marked as <strong>SUSPENDED</strong> automatically. Admins can manually restore access from the User Directory.
                        </span>
                      ) : (
                        <span className="text-amber-300">
                          ⚠️ <strong>OVERRIDE ACTIVE (OFF):</strong> Automatic account suspension is currently disabled. Failed login attempts will log rate-limiting events without changing user account status.
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-indigo-300/70">
                    <span>Rule Target: All User Accounts</span>
                    <span>Scope: Public & Scout Logins</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Strict Auth Rate Limiting & Brute-Force Protection Card */}
            <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-5 shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start space-x-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                    <ShieldAlert className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-extrabold text-base text-white">Strict Auth Rate Limiting & Brute-Force Shield</h4>
                      {localAuthSettings.strictRateLimitingEnabled ? (
                        <span className="px-3 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                          <span>ARMED & MONITORING (5 MAX / HR)</span>
                        </span>
                      ) : (
                        <span className="px-3 py-0.5 rounded-full bg-slate-700/60 text-slate-400 border border-slate-600/40 text-[10px] font-black uppercase tracking-wider">
                          STANDBY (STANDARD LIMITS)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-indigo-200/80 font-medium mt-1 leading-relaxed">
                      Monitors authentication endpoints and restricts excessive rapid login attempts from single IP addresses per hour to prevent brute force attacks, bot credential stuffing, and dictionary guessing.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 self-end md:self-center shrink-0">
                  <span className="text-xs font-extrabold text-indigo-300">
                    {localAuthSettings.strictRateLimitingEnabled ? 'Active' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={handleToggleRateLimiting}
                    className={`w-14 h-7 rounded-full transition-colors p-1 flex items-center ${
                      localAuthSettings.strictRateLimitingEnabled ? 'bg-lime-400 justify-end' : 'bg-slate-700 justify-start'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full shadow-md ${
                      localAuthSettings.strictRateLimitingEnabled ? 'bg-black' : 'bg-white'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Status Indicator & Reset Blocked IPs Panel */}
              <div className="p-4 rounded-2xl bg-indigo-900/50 border border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center space-x-2 text-xs font-bold text-indigo-200">
                    <span className="text-indigo-400 uppercase font-mono tracking-wider">Blocked IPs Status:</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-black font-mono border ${
                      blockedIPs.length > 0
                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                        : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    }`}>
                      {blockedIPs.length > 0 ? `${blockedIPs.length} Restricted IP Address(es)` : '0 Blocked IPs (All Clean)'}
                    </span>
                  </div>

                  {blockedIPs.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <span className="text-[11px] text-indigo-300/70 font-semibold">Active Restrictions:</span>
                      {blockedIPs.map(ip => (
                        <span key={ip} className="px-2 py-0.5 rounded bg-black/60 border border-rose-500/40 text-rose-300 text-[10px] font-mono font-bold">
                          {ip}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-emerald-300/80 font-medium italic">
                      No IP addresses are currently blocked by rate limit counters.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleResetBlockedIPs}
                  disabled={blockedIPs.length === 0}
                  className="px-4 py-2.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-2 shrink-0"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reset Blocked IPs</span>
                </button>
              </div>
            </div>

            {/* Searchable, Tag-Based Email Domain Whitelist Card */}
            <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-5 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-300 flex items-center justify-center shrink-0 font-bold shadow-md">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-extrabold text-base text-white">Email Domain Whitelist Manager</h4>
                      {localAuthSettings.allowedEmailDomains && localAuthSettings.allowedEmailDomains.length > 0 ? (
                        <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[10px] font-black uppercase tracking-wider">
                          {localAuthSettings.allowedEmailDomains.length} ACTIVE TAG(S)
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-wider">
                          ALL DOMAINS ALLOWED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-indigo-200/80 font-medium mt-1 leading-relaxed">
                      Restrict new athlete & scout registrations to authorized school, organization, or institutional email domains. Managed via searchable tag pills.
                    </p>
                  </div>
                </div>
              </div>

              {/* Searchable & Tag Input Controls */}
              <div className="space-y-4 pt-2 border-t border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Tag Adder Input */}
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1">
                      <Tag className="w-4 h-4 text-purple-400 absolute left-3 top-3" />
                      <input
                        type="text"
                        value={domainTagInput}
                        onChange={(e) => setDomainTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddDomainTag();
                          }
                        }}
                        placeholder="Add domain tag (e.g. college.edu)..."
                        className="w-full pl-9 pr-3 py-2.5 bg-indigo-900/80 border border-white/15 rounded-xl text-xs font-semibold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddDomainTag()}
                      className="px-4 py-2.5 bg-purple-500 hover:bg-purple-400 text-white font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1 shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Tag</span>
                    </button>
                  </div>

                  {/* Filter Search for Tags */}
                  {(localAuthSettings.allowedEmailDomains || []).length > 0 && (
                    <div className="relative">
                      <Search className="w-4 h-4 text-indigo-400 absolute left-3 top-3" />
                      <input
                        type="text"
                        value={domainTagSearch}
                        onChange={(e) => setDomainTagSearch(e.target.value)}
                        placeholder="Search active whitelist tags..."
                        className="w-full pl-9 pr-3 py-2.5 bg-indigo-900/50 border border-white/10 rounded-xl text-xs font-medium text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400"
                      />
                    </div>
                  )}
                </div>

                {/* Active Removable Tag Pills */}
                <div className="p-4 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-3">
                  <div className="flex items-center justify-between text-xs font-bold text-indigo-200">
                    <span className="flex items-center space-x-1.5">
                      <Tag className="w-3.5 h-3.5 text-purple-400" />
                      <span>Whitelisted Domain Tags:</span>
                    </span>
                    {(localAuthSettings.allowedEmailDomains || []).length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearAllTags}
                        className="text-[11px] text-rose-400 hover:text-rose-300 font-bold transition flex items-center space-x-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Clear All Tags</span>
                      </button>
                    )}
                  </div>

                  {(() => {
                    const allTags = localAuthSettings.allowedEmailDomains || [];
                    const filteredTags = allTags.filter(t => 
                      !domainTagSearch.trim() || t.toLowerCase().includes(domainTagSearch.toLowerCase().trim())
                    );

                    if (allTags.length === 0) {
                      return (
                        <div className="p-3 text-center text-xs text-indigo-300/70 font-medium italic border border-dashed border-white/10 rounded-xl">
                          No domain restrictions active. Anyone with a valid email address can register.
                        </div>
                      );
                    }

                    if (filteredTags.length === 0) {
                      return (
                        <div className="p-3 text-center text-xs text-amber-300/80 font-medium italic">
                          No domain tags match "{domainTagSearch}".
                        </div>
                      );
                    }

                    return (
                      <div className="flex flex-wrap gap-2">
                        {filteredTags.map((tag) => (
                          <div
                            key={tag}
                            className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-xl bg-purple-500/25 border border-purple-400/50 text-purple-200 text-xs font-mono font-bold shadow-sm transition hover:bg-purple-500/35"
                          >
                            <span>{tag}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveDomainTag(tag)}
                              className="w-4 h-4 rounded-full bg-purple-950 hover:bg-rose-600 text-purple-200 hover:text-white flex items-center justify-center transition"
                              title={`Remove ${tag}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Quick Domain Presets */}
                <div className="flex flex-wrap items-center gap-2 text-[11px] pt-1">
                  <span className="text-indigo-300/70 font-semibold">Quick Presets:</span>
                  {['@college.edu', '@school.org', '@ncaa.org', '@fiba.basketball', '@university.edu', '@highschool.edu'].map((preset) => {
                    const isAdded = (localAuthSettings.allowedEmailDomains || []).includes(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => handleAddDomainTag(preset)}
                        disabled={isAdded}
                        className={`px-2.5 py-1 rounded-lg border text-[10px] font-mono transition ${
                          isAdded
                            ? 'bg-purple-500/30 text-purple-200 border-purple-400/50 cursor-default opacity-60'
                            : 'bg-indigo-900/80 hover:bg-indigo-800 text-lime-300 border-white/10'
                        }`}
                      >
                        {preset} {isAdded ? '✓' : '+ Add'}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary Card: Last 24 Hours Authentication Activity Metrics */}
            {(() => {
              const last24hLogs = authLogs.filter((l) => {
                if (!l.timestamp) return true;
                const ts = l.timestamp.toLowerCase();
                if (ts.includes('just now') || ts.includes('sec') || ts.includes('min')) return true;
                if (ts.includes('hr')) {
                  const match = ts.match(/(\d+)\s*hr/);
                  if (match) {
                    return parseInt(match[1], 10) <= 24;
                  }
                  return true;
                }
                return true;
              });

              const total24hAttempts = last24hLogs.length;
              const total24hSuccesses = last24hLogs.filter(l => l.status === 'SUCCESS').length;
              const total24hFailures = last24hLogs.filter(l => l.status !== 'SUCCESS').length;
              const successRate24h = total24hAttempts > 0 ? Math.round((total24hSuccesses / total24hAttempts) * 100) : 0;
              const failureRate24h = total24hAttempts > 0 ? Math.round((total24hFailures / total24hAttempts) * 100) : 0;

              return (
                <div className="p-6 bg-gradient-to-r from-indigo-950 via-indigo-900 to-purple-950 border-2 border-lime-400/40 rounded-[2.5rem] shadow-2xl space-y-4 text-white animate-fadeIn">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3.5">
                    <div className="flex items-center space-x-3.5">
                      <div className="w-11 h-11 rounded-2xl bg-lime-400/20 border border-lime-400/40 text-lime-400 flex items-center justify-center font-bold shadow-md shrink-0">
                        <Clock className="w-6 h-6 text-lime-400 animate-pulse" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-black italic uppercase text-lg text-white">
                            24-Hour Authentication Summary
                          </h4>
                          <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase font-mono">
                            LAST 24 HOURS
                          </span>
                        </div>
                        <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                          Calculated authentication activity metrics over the past 24 hours, tracking attempts, successes, and rejections.
                        </p>
                      </div>
                    </div>
                    <div className="text-right font-mono text-xs text-indigo-300/80 shrink-0">
                      <div className="text-white font-bold text-sm">{total24hAttempts} Monitored Events</div>
                      <div className="text-[10px] text-lime-300 font-semibold">Live 24h Rolling Window</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Total Login Attempts Card */}
                    <div className="p-4 bg-indigo-950/80 border border-white/15 rounded-2xl flex items-center justify-between shadow-inner">
                      <div>
                        <div className="text-[10px] font-mono text-indigo-300 uppercase font-extrabold tracking-wider">Total Attempts (24h)</div>
                        <div className="text-3xl font-black italic text-white mt-1">{total24hAttempts}</div>
                        <div className="text-[10px] text-indigo-300/80 font-semibold mt-1">🔑 All OAuth & Password Logins</div>
                      </div>
                      <div className="p-3 bg-indigo-900/80 rounded-xl border border-white/15 text-indigo-300 shrink-0">
                        <KeyRound className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Total Successes Card */}
                    <div className="p-4 bg-emerald-950/70 border border-emerald-500/40 rounded-2xl flex items-center justify-between shadow-inner">
                      <div>
                        <div className="text-[10px] font-mono text-emerald-300 uppercase font-extrabold tracking-wider">Successful Logins (24h)</div>
                        <div className="text-3xl font-black italic text-emerald-300 mt-1">{total24hSuccesses}</div>
                        <div className="text-[10px] text-emerald-300/90 font-bold mt-1">
                          ✅ {successRate24h}% Grant Success Rate
                        </div>
                      </div>
                      <div className="p-3 bg-emerald-500/20 rounded-xl border border-emerald-500/40 text-emerald-400 shrink-0">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Total Failures Card */}
                    <div className="p-4 bg-rose-950/70 border border-rose-500/40 rounded-2xl flex items-center justify-between shadow-inner">
                      <div>
                        <div className="text-[10px] font-mono text-rose-300 uppercase font-extrabold tracking-wider">Failed / Blocked (24h)</div>
                        <div className="text-3xl font-black italic text-rose-300 mt-1">{total24hFailures}</div>
                        <div className="text-[10px] text-rose-300/90 font-bold mt-1">
                          🛡️ {failureRate24h}% Rejection / Lockout Rate
                        </div>
                      </div>
                      <div className="p-3 bg-rose-500/20 rounded-xl border border-rose-500/40 text-rose-400 shrink-0">
                        <ShieldAlert className="w-6 h-6" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Authentication Activity Log Section (Last 50 Events) */}
            <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-5 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div className="flex items-center space-x-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-lime-400/20 border border-lime-400/40 text-lime-400 flex items-center justify-center shrink-0 font-bold shadow-md">
                    <History className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-extrabold text-base text-white">Authentication Activity Log</h4>
                      <span className="px-2.5 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase font-mono">
                        LAST 50 EVENTS
                      </span>
                    </div>
                    <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                      Real-time stream of recent login & sign-up events to detect brute force patterns, domain blocks, and failed credentials.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleSimulateAuthLog}
                    className="px-3 py-2 bg-indigo-900 hover:bg-indigo-800 border border-white/15 text-lime-300 font-bold text-xs rounded-xl transition flex items-center space-x-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Simulate Event</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(authLogs, null, 2));
                      const downloadAnchor = document.createElement('a');
                      downloadAnchor.setAttribute("href", dataStr);
                      downloadAnchor.setAttribute("download", `auth_activity_log_${Date.now()}.json`);
                      document.body.appendChild(downloadAnchor);
                      downloadAnchor.click();
                      downloadAnchor.remove();
                      triggerAuthToast('Security Audit Log Exported', 'JSON file generated');
                    }}
                    className="px-3 py-2 bg-indigo-900 hover:bg-indigo-800 border border-white/15 text-indigo-200 font-bold text-xs rounded-xl transition flex items-center space-x-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Export Log</span>
                  </button>
                </div>
              </div>

              {/* Log Security Stats Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-indigo-900/40 border border-white/10 rounded-2xl">
                  <div className="text-[10px] font-mono text-indigo-300 uppercase font-bold">Total Logged</div>
                  <div className="text-lg font-black text-white">{authLogs.length} Events</div>
                </div>
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl">
                  <div className="text-[10px] font-mono text-emerald-400 uppercase font-bold">Success Rate</div>
                  <div className="text-lg font-black text-emerald-300">
                    {Math.round((authLogs.filter(l => l.status === 'SUCCESS').length / authLogs.length) * 100)}%
                  </div>
                </div>
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl">
                  <div className="text-[10px] font-mono text-amber-400 uppercase font-bold">Rate Limit Blocks</div>
                  <div className="text-lg font-black text-amber-300">
                    {authLogs.filter(l => l.status === 'BLOCKED_RATE_LIMIT').length}
                  </div>
                </div>
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-2xl">
                  <div className="text-[10px] font-mono text-purple-400 uppercase font-bold">Domain Restrictions</div>
                  <div className="text-lg font-black text-purple-300">
                    {authLogs.filter(l => l.status === 'BLOCKED_WHITELIST').length}
                  </div>
                </div>
              </div>

              {/* Filter and Search Bar */}
              <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-2">
                {/* Status Filter Tabs */}
                <div className="flex items-center space-x-1 p-1 bg-indigo-900/60 rounded-xl border border-white/10 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setAuthLogFilterStatus('ALL')}
                    className={`px-3 py-1.5 rounded-lg transition ${
                      authLogFilterStatus === 'ALL' ? 'bg-lime-400 text-black shadow-sm' : 'text-indigo-200 hover:text-white'
                    }`}
                  >
                    All ({authLogs.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthLogFilterStatus('SUCCESS')}
                    className={`px-3 py-1.5 rounded-lg transition ${
                      authLogFilterStatus === 'SUCCESS' ? 'bg-emerald-500 text-white shadow-sm' : 'text-indigo-200 hover:text-white'
                    }`}
                  >
                    Success
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthLogFilterStatus('BLOCKED')}
                    className={`px-3 py-1.5 rounded-lg transition ${
                      authLogFilterStatus === 'BLOCKED' ? 'bg-rose-500 text-white shadow-sm' : 'text-indigo-200 hover:text-white'
                    }`}
                  >
                    Blocked / Failed
                  </button>
                </div>

                {/* Search Bar */}
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 text-indigo-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    value={authLogSearch}
                    onChange={(e) => setAuthLogSearch(e.target.value)}
                    placeholder="Search by handle, email, provider, status or IP..."
                    className="w-full pl-9 pr-3 py-2 bg-indigo-900/80 border border-white/15 rounded-xl text-xs font-semibold text-white placeholder-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-lime-400"
                  />
                  {authLogSearch && (
                    <button
                      onClick={() => setAuthLogSearch('')}
                      className="absolute right-3 top-2.5 text-indigo-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Activity Log Entries List */}
              <div className="p-2 bg-indigo-900/30 rounded-2xl border border-white/10 max-h-[420px] overflow-y-auto space-y-2">
                {(() => {
                  const filtered = authLogs.filter(log => {
                    if (authLogFilterStatus === 'SUCCESS' && log.status !== 'SUCCESS') return false;
                    if (authLogFilterStatus === 'BLOCKED' && log.status === 'SUCCESS') return false;

                    if (authLogFilterProvider !== 'ALL') {
                      if (authLogFilterProvider === 'Google' && !log.provider.includes('Google')) return false;
                      if (authLogFilterProvider === 'Apple' && !log.provider.includes('Apple')) return false;
                      if (authLogFilterProvider === 'Facebook' && !log.provider.includes('Facebook')) return false;
                      if (authLogFilterProvider === 'Email' && !log.provider.includes('Email')) return false;
                    }

                    if (authLogSearch.trim()) {
                      const q = authLogSearch.toLowerCase().trim();
                      const matchEmail = log.userEmail.toLowerCase().includes(q);
                      const matchHandle = log.handle.toLowerCase().includes(q);
                      const matchIp = log.ipAddress.toLowerCase().includes(q);
                      const matchLoc = log.location.toLowerCase().includes(q);
                      const matchStatus = log.status.toLowerCase().includes(q);
                      const matchProvider = log.provider.toLowerCase().includes(q);
                      return matchEmail || matchHandle || matchIp || matchLoc || matchStatus || matchProvider;
                    }

                    return true;
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="p-8 text-center text-xs text-indigo-300/60 font-semibold space-y-2">
                        <ShieldAlert className="w-8 h-8 text-indigo-400/40 mx-auto" />
                        <p>No authentication log events match your search criteria.</p>
                      </div>
                    );
                  }

                  return filtered.map((item) => {
                    const isBlockedIpMatch = blockedIPs.includes(item.ipAddress);

                    let statusBadge = (
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold font-mono">
                        200 OK • SUCCESS
                      </span>
                    );

                    if (item.status === 'BLOCKED_RATE_LIMIT') {
                      statusBadge = (
                        <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold font-mono">
                          429 RATE LIMITED
                        </span>
                      );
                    } else if (item.status === 'BLOCKED_WHITELIST') {
                      statusBadge = (
                        <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[10px] font-black uppercase font-mono">
                          403 DOMAIN BLOCKED
                        </span>
                      );
                    } else if (item.status === 'INVALID_PASSWORD') {
                      statusBadge = (
                        <span className="px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-bold font-mono">
                          401 BAD CREDENTIALS
                        </span>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className={`p-3 rounded-xl transition flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs ${
                          isBlockedIpMatch
                            ? 'border-2 border-rose-500 bg-rose-950/80 shadow-lg shadow-rose-950/60 ring-2 ring-rose-500/30'
                            : 'bg-indigo-950/80 hover:bg-indigo-950 border border-white/5 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center space-x-3 shrink-0">
                          <div className="w-8 h-8 rounded-lg bg-indigo-900 border border-white/10 flex items-center justify-center shrink-0">
                            {item.provider.includes('Google') && <Chrome className="w-4 h-4 text-rose-400" />}
                            {item.provider.includes('Apple') && <Lock className="w-4 h-4 text-white" />}
                            {item.provider.includes('Facebook') && <Users className="w-4 h-4 text-blue-400" />}
                            {item.provider.includes('Email') && <Mail className="w-4 h-4 text-lime-400" />}
                          </div>

                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-extrabold text-white">{item.userEmail}</span>
                              <span className="text-[10px] text-indigo-300/80 font-mono">{item.handle}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-[10px] text-indigo-300/60 font-mono mt-0.5">
                              <span>{item.provider}</span>
                              <span>•</span>
                              <span>{item.timestamp}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 shrink-0">
                          {isBlockedIpMatch && (
                            <span className="px-2.5 py-0.5 rounded-full bg-rose-600 text-white border border-rose-400 text-[10px] font-black uppercase tracking-wider font-mono shadow-md flex items-center space-x-1 shrink-0 animate-pulse">
                              <AlertTriangle className="w-3 h-3 text-amber-300 shrink-0" />
                              <span>RESTRICTED IP MATCH</span>
                            </span>
                          )}
                          <div className="text-right text-[10px] font-mono text-indigo-300/80">
                            <div className={isBlockedIpMatch ? "font-extrabold text-rose-300 underline" : ""}>{item.ipAddress}</div>
                            <div className="text-indigo-400/60">{item.location}</div>
                          </div>
                          {statusBadge}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Standard Email Always Enabled Card */}
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-lime-400/20 border border-lime-400/30 text-lime-400 flex items-center justify-center shrink-0 font-bold">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-extrabold text-white">Email & Password Direct Sign-In</span>
                    <span className="px-2 py-0.5 rounded-full bg-lime-400 text-black text-[9px] font-black italic uppercase">
                      CORE SYSTEM MANDATE
                    </span>
                  </div>
                  <p className="text-indigo-200/70 text-[11px] mt-0.5">
                    Email & Password registration, verification links, and password resets remain permanently active.
                  </p>
                </div>
              </div>

              <div className="px-3 py-1 bg-lime-400/20 text-lime-300 font-black italic text-[11px] rounded-xl border border-lime-400/30 shrink-0 uppercase">
                ✓ Always Active
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= SECTION 10: EMAIL VERIFICATION LOGS & SMTP ================= */}
      {adminSection === 'email_logs' && (
        <EmailLogsTab onTriggerToast={triggerAuthToast} />
      )}

      {/* ================= SECTION 11: EMAIL HTML TEMPLATE LIVE CODE EDITOR ================= */}
      {adminSection === 'email_template' && (
        <EmailTemplateEditor onTriggerToast={triggerAuthToast} />
      )}

      {/* ================= MODAL: RESET ENTIRE SITE (LIVE RELEASE) ================= */}
      {isSiteResetModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-lg bg-indigo-950 border-2 border-rose-500 rounded-[2.5rem] p-6 sm:p-8 space-y-6 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3 text-rose-500">
                <div className="p-2.5 bg-rose-500/20 rounded-2xl border border-rose-500/40">
                  <ShieldAlert className="w-6 h-6 text-rose-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black italic uppercase text-white">Live Launch Site Wipe</h3>
                  <span className="text-[10px] font-mono text-rose-400 uppercase tracking-widest font-bold">DANGER ZONE • CRITICAL ADMIN ACTION</span>
                </div>
              </div>
              <button onClick={() => setIsSiteResetModalOpen(false)} className="text-indigo-300 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-3 text-xs text-indigo-200/90 leading-relaxed font-semibold">
              <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-300 space-y-2">
                <p className="font-bold flex items-center space-x-1.5">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>This action will permanently purge platform data for Live Launch:</span>
                </p>
                <ul className="list-disc pl-5 space-y-1 text-[11px] text-rose-200/80 font-normal">
                  <li>Deletes all registered user accounts & custom athlete profiles</li>
                  <li>Resets all win/loss match stats, points, rebounds, assists & levels to 0</li>
                  <li>Clears all custom pickup game reservations and active chats</li>
                  <li>Clears local browser caches & logs out active sessions</li>
                </ul>
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase text-indigo-300 mb-1">
                  Type <span className="text-white font-mono bg-rose-950 px-1.5 py-0.5 rounded border border-rose-500/40">RESET</span> to confirm site wipe:
                </label>
                <input
                  type="text"
                  value={siteResetConfirmInput}
                  onChange={(e) => {
                    setSiteResetConfirmInput(e.target.value);
                    setSiteResetError('');
                  }}
                  placeholder="Type RESET in all caps..."
                  className="w-full bg-indigo-900 border border-white/20 rounded-xl px-4 py-3 text-sm font-black font-mono text-white placeholder-indigo-400/40 focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
              </div>

              {siteResetError && (
                <div className="p-3 bg-rose-500/20 border border-rose-500/40 rounded-xl text-xs text-rose-300 font-bold flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{siteResetError}</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3 pt-2 border-t border-white/10">
              <button
                onClick={() => setIsSiteResetModalOpen(false)}
                className="flex-1 py-3 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black italic uppercase rounded-2xl text-xs transition"
              >
                Cancel & Abort
              </button>

              <button
                onClick={() => {
                  if (siteResetConfirmInput.trim() !== 'RESET') {
                    setSiteResetError('Please type "RESET" exactly as shown to authorize live site wipe.');
                    return;
                  }
                  if (onResetEntireSite) {
                    onResetEntireSite();
                  }
                  setIsSiteResetModalOpen(false);
                  setActionNotice('Site reset completed! All users and stats purged for live release.');
                  addAuditLog('CRITICAL: Administrator executed Live Release Site Reset Wipe', 'delete');
                }}
                className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 text-white font-black italic uppercase rounded-2xl text-xs transition shadow-lg shadow-rose-600/30 flex items-center justify-center space-x-1.5"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Execute Live Site Wipe</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reset Entire Application Settings Secondary Portal Confirmation Modal */}
      <ResetEntireSettingsConfirmationModal
        isOpen={isResetSettingsConfirmModalOpen}
        onClose={() => setIsResetSettingsConfirmModalOpen(false)}
        onConfirmReset={handleResetEntireApplicationSettings}
      />
    </div>
  );
};
